import type { ListViewState } from '../../../context/AdminListStateContext'
import { TablePageSelection, TableRowSelection } from '../../ui/TableSelection'
import { useSmartTable } from '../../../hooks/useSmartTable'
import { TableQueryControls, TableDisplayControls, TableSortHeader } from '../../ui/SmartTableControls'
import { jobTableDefinition, type JobTableFilters } from './productivityTables'
import { AdminTableContainer } from './AdminUiPrimitives'
import { locationDisplayAddress } from '../../../services/locationAddress'
import {NativeJobClassifications} from './NativeJobClassifications'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  approveJobAssignment,
  approveShiftApplication,
  archiveJob,
  archiveJobShift,
  cancelJobShift,
  cancelJob,
  cancelJobAssignment,
  cancelSystemJobShift,
  createJob,
  createJobApplication,
  createJobAssignment,
  createJobShift,
  createJobVisibilityRule,
  createSkill,
  deleteJob,
  deleteJobAssignment,
  deleteJobShift,
  deleteJobVisibilityRule,
  duplicateJobShift,
  listJobApplications,
  listJobAssignments,
  listJobShifts,
  listJobShiftMatches,
  listRankedShiftApplicants,
  listJobVisibilityRules,
  listJobsPage,
  listSkills,
  publishJobShift,
  purgeDemoJob,
  purgeDemoJobAssignment,
  purgeDemoJobShift,
  rejectShiftApplication,
  restoreJob,
  restoreJobAssignment,
  restoreJobShift,
  revokeJobAssignment,
  getJobDeleteImpact,
  getJobAssignmentDeleteImpact,
  getJobShiftDeleteImpact,
  updateJob,
  updateJobAssignment,
  updateJobShift,
  updateJobStatus,
  apiErrorMessage,
} from '../../../services/access'
import type {
  AccessibleLocation,
  GrantedClientLocation,
  Job,
  JobApplication,
  JobAssignment,
  JobAssignmentStatus,
  JobPayload,
  JobShift,
  JobShiftStatus,
  JobShiftWorkerMatch,
  ShiftApplication,
  JobStatus,
  JobVisibilityRule,
  JobVisibilityScope,
  JobVisibilityType,
  Organization,
  OrganizationBranding,
  OrganizationRelationship,
  RelationshipAccessEntry,
  Skill,
  WorkMode,
  WorkerOrgRelationship,
} from '../../../types/access'
import { organizationLabel } from './adminPanelUtils'
import { parseSkillTags } from '../../../utils/skillTags'
import { ConfirmDialog, DetailDrawer, EmptyState, EntityActionMenu, ErrorBanner, FormModal, LoadingState, PaginationControls } from './AdminUiPrimitives'
import { BrandPair, findBrand } from './BrandDisplay'
import { ShiftImport } from '../ShiftImport'
import { getAccessibleLocationOwnerOrgId, type DelegatedJobContext, type LocationJobDraft } from './adminNavigation'
import { SkillPicker } from '../../ui/SkillPicker'
import type { SkillCatalogResult } from '../../../services/skills'
import { useI18n } from '../../../i18n/I18nContext'
import AssignmentLifecycleBadge from '../AssignmentLifecycleBadge'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'
import { useAdminExperience } from '../../../context/AdminExperienceContext'
import { PostWorkWizard } from './PostWorkWizard'

interface JobsPanelProps {
  organizations: Organization[]
  brandingRows?: OrganizationBranding[]
  accessibleLocations: AccessibleLocation[]
  organizationRelationships?: OrganizationRelationship[]
  workerRelationships: WorkerOrgRelationship[]
  selectedOrgId: string
  canViewJobs: boolean
  canManageJobs: boolean
  canManageApplications: boolean
  canManageAssignments: boolean
  canApproveAssignments: boolean
  canManageSkills: boolean
  canViewPayRate: boolean
  canViewBillRate: boolean
  lifecyclePermissions?: {
    canArchive: boolean
    canRestore: boolean
    canDeleteIfEmpty: boolean
    canPurgeDemoData: boolean
  }
  initialSelectedJobId?: string
  initialJobDraft?: LocationJobDraft | null
  delegatedJobContext?: DelegatedJobContext | null
  onConsumeInitialJobDraft?: () => void
  onConsumeDelegatedJobContext?: () => void
  onSelectOrg: (orgId: string) => void
  onOpenSchedule?: () => void
  relationshipAccess?: RelationshipAccessEntry[]
  openPostWorkNonce?: number
  onPostWorkClosed?: () => void
}

const jobStatuses: JobStatus[] = ['draft', 'posted', 'open', 'partially_filled', 'filled', 'in_progress', 'completed', 'cancelled']
const visibilityTypes: JobVisibilityType[] = ['private', 'own_workers', 'specific_agencies', 'shared_agencies', 'company_only', 'marketplace_public', 'specific_workers']
const visibilityScopes: JobVisibilityScope[] = ['agency', 'company', 'worker_pool', 'specific_worker', 'public_marketplace']
const shiftStatuses: JobShiftStatus[] = ['draft', 'published', 'open', 'partially_filled', 'filled', 'in_progress', 'completed', 'cancelled']
const assignmentStatuses: JobAssignmentStatus[] = ['offered', 'accepted', 'declined', 'pending_worker_acceptance', 'pending_company_approval', 'confirmed', 'rejected', 'cancelled', 'in_progress', 'completed', 'no_show']
const workModes: WorkMode[] = ['onsite', 'hybrid', 'remote']
const requirementTypes = ['required', 'preferred', 'certification'] as const
const minimumLevels = ['', 'beginner', 'intermediate', 'advanced', 'expert', 'certified'] as const
const terminalLifecycleStates = new Set(['completed', 'cancelled', 'expired', 'unavailable', 'rejected', 'no_show', 'withdrawn'])
type LifecycleFilter = 'active' | 'history' | 'all'
type JobSkillDraft = {
  skill: SkillCatalogResult
  requirementType: typeof requirementTypes[number]
  minimumLevel: typeof minimumLevels[number]
}
type WorkerRelationshipOption = WorkerOrgRelationship & { worker_profile_id: string }

const normalizeSkillIndustries = (industries?: Array<{ id?: string; name: string }>) =>
  (industries || []).map((industry, index) => ({
    id: industry.id ?? `industry-${index}-${industry.name}`,
    name: industry.name,
  }))

const toInputDateTime = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 16)
}

const toPayloadDateTime = (value: string) => value ? new Date(value).toISOString() : ''

const compactStatus = (value?: string | null) => (value || '-').replace(/_/g, ' ')

const titleCase = (value?: string | null) => compactStatus(value).replace(/\b\w/g, (letter) => letter.toUpperCase())
const translateTitleCase = (t: (text: string, vars?: Record<string, string | number>) => string, value?: string | null) => t(titleCase(value))
const lifecycleLabel = (value?: string | null) => {
  switch (value) {
    case 'draft':
      return 'Draft'
    case 'posted':
    case 'open':
    case 'partially_filled':
      return 'Posted'
    case 'filled':
    case 'confirmed':
    case 'pending_company_approval':
    case 'pending_worker_acceptance':
      return 'Filled'
    case 'active':
    case 'in_progress':
      return 'Active'
    case 'completed':
      return 'Completed'
    case 'expired':
      return 'Expired'
    case 'cancelled':
      return 'Cancelled'
    case 'unavailable':
    case 'rejected':
    case 'no_show':
    case 'withdrawn':
      return 'Unavailable'
    default:
      return titleCase(value)
  }
}

const rateValue = (value?: number | string | null) => value === null || value === undefined || value === '' ? '' : String(value)
const rateNumber = (value: string | number) => value === '' ? null : Number(value)
const currencyValue = (value?: string | null) => (value || 'CAD').toUpperCase()
const validRate = (value?: string | number | null) => value === null || value === undefined || value === '' || Number.isFinite(Number(value))
const positiveInteger = (value?: number | string | null) => Number.isInteger(Number(value)) && Number(value) > 0
const rateLabel = (rate?: number | string | null, currency = 'CAD') => {
  if (rate === null || rate === undefined || rate === '') return '-'
  const amount = Number(rate)
  if (!Number.isFinite(amount)) return '-'
  const code = currencyValue(currency)
  return code === 'CAD' || code === 'USD' ? `$${amount.toFixed(2)} ${code}/hr` : `${amount.toFixed(2)} ${code}/hr`
}
const localizedRateLabel = (t: (text: string, vars?: Record<string, string | number>) => string, rate?: number | string | null, currency = 'CAD') => {
  const label = rateLabel(rate, currency)
  return label === '-' ? t('Not set') : label
}

const locationLabel = (location?: AccessibleLocation | null) => {
  if (!location) return 'No location'
  const address = locationDisplayAddress(location)
  const ownerName = location.ownerOrgName || location.owner_org_name || location.company_name || ''
  const label = location.name || location.label || 'Unnamed location'
  if (ownerName && address) return `${ownerName} - ${label} - ${address}`
  if (ownerName) return `${ownerName} / ${label}`
  return address ? `${label} - ${address}` : label
}

type ShiftFormState = {
  locationId: string
  startsAt: string
  endsAt: string
  workersNeeded: number
  status: JobShiftStatus
  workMode: WorkMode | ''
  matchingRadiusKm: string | number
  payRate: string | number
  billRate: string | number
  currencyCode: string
}

type ShiftFormDraft = Omit<ShiftFormState, 'locationId'>

const toShiftFormDraft = (shift: JobShift): ShiftFormDraft => ({
  startsAt: toInputDateTime(shift.starts_at),
  endsAt: toInputDateTime(shift.ends_at),
  workersNeeded: shift.workers_needed,
  status: shift.status,
  workMode: shift.work_mode || '',
  matchingRadiusKm: shift.matching_radius_km ?? '',
  payRate: rateValue(shift.pay_rate),
  billRate: rateValue(shift.bill_rate),
  currencyCode: shift.currency_code || '',
})

const getLocationOrgId = (location?: AccessibleLocation | null) => (
  location ? getAccessibleLocationOwnerOrgId(location) : ''
)

const workerLabel = (worker?: WorkerOrgRelationship | null) => {
  if (!worker) return ''
  const name = worker.display_name || [worker.first_name, worker.last_name].filter(Boolean).join(' ').trim()
  return name || worker.worker_email || worker.worker_phone || 'Worker profile'
}

const shiftLabel = (shift?: JobShift | null) => {
  if (!shift) return ''
  return `${new Date(shift.starts_at).toLocaleString()} - ${titleCase(shift.status)}`
}

const statusClass = (status?: string | null) => `status-pill status-pill--${String(status || '').replace(/[^a-z0-9_-]/gi, '_')}`
const statusBadge = (status?: string | null, title?: string, t?: (text: string, vars?: Record<string, string | number>) => string) => (
  <span className={statusClass(status)} title={title || (status && lifecycleLabel(status) !== titleCase(status) ? titleCase(status) : undefined)}>
    {t ? t(lifecycleLabel(status)) : lifecycleLabel(status)}
  </span>
)

const isPastDate = (value?: string | null) => {
  if (!value) return false
  const time = new Date(value).getTime()
  return Number.isFinite(time) && time < Date.now()
}

const shiftLifecycleStatus = (shift: JobShift) => {
  if (shift.status === 'cancelled' || shift.status === 'completed' || shift.status === 'in_progress' || shift.status === 'filled') return shift.status
  return isPastDate(shift.ends_at) ? 'expired' : shift.status
}

const assignmentLifecycleStatus = (assignment: JobAssignment) => {
  if (assignment.status === 'in_progress' || assignment.status === 'completed' || assignment.status === 'cancelled' || assignment.status === 'rejected' || assignment.status === 'no_show') return assignment.status
  return assignment.cancelled_at ? 'cancelled' : assignment.status
}

const jobLifecycleStatus = (job: Job) => {
  if (job.status === 'completed' || job.status === 'cancelled' || job.status === 'filled' || job.status === 'in_progress' || job.status === 'draft') return job.status
  if ((job.status as string) === 'pending_company_approval') return 'posted'
  if (job.cancelled_at) return 'cancelled'
  if ((job.shift_count || 0) > 0 && (job.open_shift_count || 0) === 0 && (job.assigned_shift_count || 0) === 0 && (job.cancelled_shift_count || 0) === (job.shift_count || 0)) return 'cancelled'
  return job.status
}

const isHistoricalLifecycle = (status?: string | null) => terminalLifecycleStates.has(String(status || ''))

const skillChips = (items?: string[]) => (items || []).filter(Boolean).map((item) => <span key={item} className="admin-chip">{item}</span>)

const matchWorkerLabel = (match: JobShiftWorkerMatch) => (
  match.worker_display_name || match.worker_email || match.worker_phone || 'Worker'
)

const matchMissingRequiredSkills = (match: JobShiftWorkerMatch) => (
  (match.missingRequiredSkills || match.missing_skill_names || []).filter(Boolean)
)

const matchBelowRequiredLevelSkills = (match: JobShiftWorkerMatch) => (
  (match.belowRequiredLevelSkills || match.below_required_level_skill_names || []).filter(Boolean)
)

const matchCertificatePendingSkills = (match: JobShiftWorkerMatch) => (
  (match.certificatePendingSkills || match.certificate_pending_skill_names || []).filter(Boolean)
)

const matchCertificateExpiredSkills = (match: JobShiftWorkerMatch) => (
  (match.certificateExpiredSkills || match.certificate_expired_skill_names || []).filter(Boolean)
)

const matchEligibilityReasons = (match: JobShiftWorkerMatch) => (
  (match.eligibilityReasons || match.eligibility_reasons || []).filter(Boolean)
)

const matchIsEligible = (match: JobShiftWorkerMatch) => match.eligible !== false && matchMissingRequiredSkills(match).length === 0

const localizedMatchDistanceDiagnostic = (t: (text: string, vars?: Record<string, string | number>) => string, match: JobShiftWorkerMatch) => {
  const meters = match.distanceMeters ?? (match.distance_km == null ? null : Number(match.distance_km) * 1000)
  const radiusMeters = match.effectiveRadiusMeters ?? (match.effective_radius_km == null ? null : Number(match.effective_radius_km) * 1000)
  const parts = []
  if (meters != null && Number.isFinite(Number(meters))) parts.push(t('{km} km away', { km: (Number(meters) / 1000).toFixed(1) }))
  if (radiusMeters != null && Number.isFinite(Number(radiusMeters))) parts.push(t('{km} km limit', { km: (Number(radiusMeters) / 1000).toFixed(1) }))
  const source = match.locationSource || match.location_source
  if (source) parts.push(t('source: {source}', { source: t(titleCase(String(source))) }))
  const reason = match.eligibility_reason
  if (reason) parts.push(t(titleCase(reason)))
  return parts.join(' / ')
}

const shiftOpenSlots = (shift?: JobShift | null) => Math.max(0, Number(shift?.workers_needed || 0) - Number(shift?.confirmed_assignments_count || 0))

const matchAssignmentBlockReason = (match: JobShiftWorkerMatch, shift?: JobShift | null) => {
  const missingSkills = matchMissingRequiredSkills(match)
  const belowLevelSkills = matchBelowRequiredLevelSkills(match)
  const certificatePendingSkills = matchCertificatePendingSkills(match)
  const certificateExpiredSkills = matchCertificateExpiredSkills(match)
  if (missingSkills.length > 0 || belowLevelSkills.length > 0 || certificatePendingSkills.length > 0 || certificateExpiredSkills.length > 0 || match.eligible === false) {
    if (missingSkills.length) return `Missing required skills: ${missingSkills.join(', ')}`
    if (belowLevelSkills.length) return `Below required level: ${belowLevelSkills.join(', ')}`
    if (certificatePendingSkills.length) return `Certificate pending: ${certificatePendingSkills.join(', ')}`
    if (certificateExpiredSkills.length) return `Certificate expired: ${certificateExpiredSkills.join(', ')}`
    return matchEligibilityReasons(match).join(', ') || 'Missing required skills'
  }
  if (shift && shiftOpenSlots(shift) <= 0) return 'Shift already filled'
  if (match.assignment_id) return 'Worker already assigned'
  return ''
}

const localizedMatchAssignmentBlockReason = (t: (text: string, vars?: Record<string, string | number>) => string, match: JobShiftWorkerMatch, shift?: JobShift | null) => {
  const missingSkills = matchMissingRequiredSkills(match)
  const belowLevelSkills = matchBelowRequiredLevelSkills(match)
  const certificatePendingSkills = matchCertificatePendingSkills(match)
  const certificateExpiredSkills = matchCertificateExpiredSkills(match)
  if (missingSkills.length > 0 || belowLevelSkills.length > 0 || certificatePendingSkills.length > 0 || certificateExpiredSkills.length > 0 || match.eligible === false) {
    if (missingSkills.length) return t('Missing required skills: {skills}', { skills: missingSkills.join(', ') })
    if (belowLevelSkills.length) return t('Below required level: {skills}', { skills: belowLevelSkills.join(', ') })
    if (certificatePendingSkills.length) return t('Certificate pending: {skills}', { skills: certificatePendingSkills.join(', ') })
    if (certificateExpiredSkills.length) return t('Certificate expired: {skills}', { skills: certificateExpiredSkills.join(', ') })
    return matchEligibilityReasons(match).map((reason) => t(titleCase(reason))).join(', ') || t('Missing required skills')
  }
  if (shift && shiftOpenSlots(shift) <= 0) return t('Shift already filled')
  if (match.assignment_id) return t('Worker already assigned')
  return ''
}

const renderMatchDiagnosticChips = (t: (text: string, vars?: Record<string, string | number>) => string, match: JobShiftWorkerMatch) => {
  const chips: React.ReactNode[] = []
  matchMissingRequiredSkills(match).forEach((skill) => chips.push(<span key={`missing-${skill}`} className="admin-chip">{t('Missing: {skill}', { skill })}</span>))
  matchBelowRequiredLevelSkills(match).forEach((skill) => chips.push(<span key={`below-${skill}`} className="admin-chip">{t('Below required level: {skill}', { skill })}</span>))
  matchCertificatePendingSkills(match).forEach((skill) => chips.push(<span key={`pending-${skill}`} className="admin-chip">{t('Certificate pending: {skill}', { skill })}</span>))
  matchCertificateExpiredSkills(match).forEach((skill) => chips.push(<span key={`expired-${skill}`} className="admin-chip">{t('Certificate expired: {skill}', { skill })}</span>))
  ;(match.eligibilityDiagnostics || match.eligibility_diagnostics || []).forEach((diagnostic, index) => {
    const name = diagnostic.canonicalName || diagnostic.canonical_name || diagnostic.skillName || diagnostic.skill_name || t('Skill')
    const code = diagnostic.diagnostic || 'eligible'
    if (['missing_skill', 'below_required_level', 'certificate_pending', 'certificate_expired'].includes(code)) return
    chips.push(<span key={`diag-${index}-${code}-${name}`} className="admin-chip">{t(titleCase(code))}: {name}</span>)
  })
  if (!chips.length && matchIsEligible(match)) chips.push(<span key="eligible" className="admin-chip">{t('Eligible')}</span>)
  return chips
}

const relationshipWorkerOptionLabel = (worker: WorkerRelationshipOption) => {
  const context = [worker.relationship_type ? titleCase(worker.relationship_type) : '', worker.org_name || '']
    .filter(Boolean)
    .join(' / ')
  return context ? `${workerLabel(worker)} - ${context}` : workerLabel(worker)
}

const conflictSeverity = (conflict: { severity?: string } = {}) => conflict.severity === 'warning' ? 'Warning' : 'Blocked'
const conflictLabel = (conflict: { conflictType?: string; conflict_type?: string; message?: string } = {}) => {
  const type = conflict.conflictType || conflict.conflict_type
  switch (type) {
    case 'worker_unavailable':
      return 'Worker unavailable'
    case 'outside_availability':
      return 'Outside declared availability'
    case 'overlapping_assignment':
      return 'Overlapping assignment'
    case 'shift_overfilled':
      return 'Overfilled shift'
    case 'missing_location_geofence':
      return 'Missing geofence warning'
    case 'shift_not_actionable':
    case 'shift_expired':
      return 'Cancelled/completed/past shift blocked'
    default:
      return titleCase(type || conflict.message || 'Schedule conflict')
  }
}

const jobSelectionId = (job: Job) => job.id
const jobSelectionEligible = (job: Job) => typeof job.id === 'string' && job.id.length > 0

export const JobsPanel: React.FC<JobsPanelProps> = ({
  organizations,
  brandingRows = [],
  accessibleLocations,
  organizationRelationships = [],
  workerRelationships,
  selectedOrgId,
  canViewJobs,
  canManageJobs,
  canManageApplications,
  canManageAssignments,
  canApproveAssignments,
  canManageSkills,
  canViewPayRate,
  canViewBillRate,
  lifecyclePermissions,
  initialSelectedJobId,
  initialJobDraft,
  delegatedJobContext,
  onConsumeInitialJobDraft,
  onConsumeDelegatedJobContext,
  onSelectOrg,
  onOpenSchedule,
  relationshipAccess = [],
  openPostWorkNonce = 0,
  onPostWorkClosed,
}) => {
  const { t } = useI18n()
  const { isSimple, isAdvanced } = useAdminExperience()
  const industryLabels = useIndustryTerminology()
  const tc = (value?: string | null) => translateTitleCase(t, value)
  const rate = (value?: number | string | null, currency = 'CAD') => localizedRateLabel(t, value, currency)
  const industryTerm = (key: 'job' | 'jobs' | 'shift' | 'shifts' | 'assignment' | 'assignments' | 'worker' | 'workers' | 'location') => industryLabels.label(key)
  const industryTermLower = (key: Parameters<typeof industryTerm>[0]) => industryTerm(key).toLowerCase()
  const createJobLabel = industryLabels.explicitIndustry ? t('Create {resource}', { resource: industryTermLower('job') }) : t('Create job')
  const assignWorkerLabel = industryLabels.explicitIndustry ? t('Assign {worker}', { worker: industryTermLower('worker') }) : t('Assign worker')
  const canArchiveJobs = lifecyclePermissions?.canArchive ?? canManageJobs
  const canRestoreJobs = lifecyclePermissions?.canRestore ?? canManageJobs
  const canDeleteEmptyJobs = lifecyclePermissions?.canDeleteIfEmpty ?? canManageJobs
  const postWorkTriggerRef = useRef<HTMLButtonElement | null>(null)
  const postWorkOpenedFromGlobalRef = useRef(false)
  const [skills, setSkills] = useState<Skill[]>([])
  const [selectedJobId, setSelectedJobId] = useState('')
  const [shifts, setShifts] = useState<JobShift[]>([])
  const [selectedMatchShiftId, setSelectedMatchShiftId] = useState('')
  const [shiftMatches, setShiftMatches] = useState<JobShiftWorkerMatch[]>([])
  const [rankedApplicants, setRankedApplicants] = useState<ShiftApplication[]>([])
  const [matchSearch, setMatchSearch] = useState('')
  const [visibilityRules, setVisibilityRules] = useState<JobVisibilityRule[]>([])
  const [applications, setApplications] = useState<JobApplication[]>([])
  const [assignments, setAssignments] = useState<JobAssignment[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showJobModal, setShowJobModal] = useState(false)
  const [showPostWorkWizard, setShowPostWorkWizard] = useState(false)
  const [showJobDrawer, setShowJobDrawer] = useState(false)
  const [showShiftModal, setShowShiftModal] = useState(false)
  const [showVisibilityModal, setShowVisibilityModal] = useState(false)
  const [showApplicationModal, setShowApplicationModal] = useState(false)
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [showSkillModal, setShowSkillModal] = useState(false)
  const [showShiftImportModal, setShowShiftImportModal] = useState(false)
  const [repeatShiftCount, setRepeatShiftCount] = useState(1)
  const [editingJob, setEditingJob] = useState<Job | null>(null)
  const [editingShift, setEditingShift] = useState<JobShift | null>(null)
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; confirmLabel?: string; destructive?: boolean; onConfirm: () => Promise<void> } | null>(null)

  useEffect(() => {
    if (openPostWorkNonce > 0) {
      postWorkOpenedFromGlobalRef.current = true
      setShowPostWorkWizard(true)
    }
  }, [openPostWorkNonce])

  const [detailLifecycle, setDetailLifecycle] = useState<LifecycleFilter>('active')

  const [jobDelegatedRelId, setJobDelegatedRelId] = useState('')
  const [jobForm, setJobForm] = useState<JobPayload>({
    createdByOrgId: selectedOrgId,
    companyOrgId: '',
    managingAgencyOrgId: '',
    locationId: '',
    locationTenantId: null,
    title: '',
    description: '',
    jobType: '',
    status: 'draft',
    visibilityType: 'private',
    approvalRequired: false,
    workMode: 'onsite',
    matchingRadiusKm: 25,
    payRate: '',
    billRate: '',
    currencyCode: 'CAD',
    requiredSkillTags: [],
  })
  const [skillTagDraft, setSkillTagDraft] = useState('')
  const [jobSkillDrafts, setJobSkillDrafts] = useState<JobSkillDraft[]>([])
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [statusDraft, setStatusDraft] = useState<JobStatus>('draft')

  const [shiftForm, setShiftForm] = useState<ShiftFormState>({
    locationId: '',
    startsAt: '',
    endsAt: '',
    workersNeeded: 1,
    status: 'open' as JobShiftStatus,
    workMode: '' as WorkMode | '',
    matchingRadiusKm: '',
    payRate: '',
    billRate: '',
    currencyCode: '',
  })
  const [shiftDrafts, setShiftDrafts] = useState<Record<string, ShiftFormDraft>>({})
  const [visibilityForm, setVisibilityForm] = useState({
    visibilityScope: 'agency' as JobVisibilityScope,
    visibleToOrgId: '',
    visibleToWorkerProfileId: '',
    canApply: true,
    canAssign: false,
    canViewRates: false,
  })
  const [applicationForm, setApplicationForm] = useState({
    workerProfileId: '',
    jobShiftId: '',
    applyingThroughOrgId: '',
    coverNote: '',
  })
  const [assignmentForm, setAssignmentForm] = useState({
    workerProfileId: '',
    jobShiftId: '',
    applicationId: '',
    assignedByOrgId: selectedOrgId,
    supplyingAgencyOrgId: '',
    companyOrgId: '',
    status: 'confirmed' as JobAssignmentStatus,
    payRate: '',
    billRate: '',
    rateCurrency: 'CAD',
  })
  const [assignmentStatusDrafts, setAssignmentStatusDrafts] = useState<Record<string, JobAssignmentStatus>>({})
  const [assignmentRateDrafts, setAssignmentRateDrafts] = useState<Record<string, { payRate: string; billRate: string; rateCurrency: string }>>({})
  const [skillForm, setSkillForm] = useState({ name: '', category: '', description: '' })
  const validLocations = useMemo(() => accessibleLocations.filter((location) => {
    const name = String(location.name || location.label || location.location_key || '').trim()
    const status = String(location.status || '').toLowerCase()
    return Boolean(name) && !['inactive', 'archived', 'deleted'].includes(status)
  }), [accessibleLocations])
  const locationById = useMemo(() => new Map(validLocations.map((location) => [location.id, location])), [validLocations])
  const refineJobs = useCallback((items: Job[], view: ListViewState) => {
    const term = view.search.trim().toLowerCase()
    return items.filter((job) => {
      const lifecycleStatus = jobLifecycleStatus(job)
      if ((view.filters.lifecycle || 'active') === 'active' && isHistoricalLifecycle(lifecycleStatus)) return false
      if ((view.filters.lifecycle || 'active') === 'history' && !isHistoricalLifecycle(lifecycleStatus)) return false
      if (!term) return true
      return [
      job.title,
      job.company_org_name || organizationLabel(organizations, job.company_org_id),
      job.managing_agency_org_name || organizationLabel(organizations, job.managing_agency_org_id),
      job.location_name || locationLabel(locationById.get(job.location_id || '')),
      lifecycleLabel(lifecycleStatus),
      ...(job.required_skill_tags || []),
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term))
    })
  }, [locationById, organizations])
  const tableDefinition = useMemo(() => jobTableDefinition(organizations, jobStatuses, visibilityTypes), [organizations])
  const jobList = useSmartTable<Job, JobTableFilters>(tableDefinition, {
    enabled: canViewJobs,
    refineItems: refineJobs,
    selection: { enabled: canViewJobs && isAdvanced, getId: jobSelectionId, isEligible: jobSelectionEligible },
    fetchPage: (query, signal) => canViewJobs
      ? listJobsPage({ ...query }, signal)
      : Promise.resolve({
        items: [],
        pagination: { page: query.page || 1, pageSize: query.pageSize || 25, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
        sort: { sortBy: query.sortBy, sortDir: query.sortDir || 'desc' },
        filters: { ...query },
      }),
  })
  const jobs = jobList.items
  const filters = { search: jobList.search, status: jobList.filters.status || '', companyOrgId: jobList.filters.companyOrgId || '',
    managingAgencyOrgId: jobList.filters.managingAgencyOrgId || '', visibilityType: jobList.filters.visibilityType || '',
    lifecycle: (jobList.filters.lifecycle || 'active') as LifecycleFilter }

  useEffect(() => {
    if (initialSelectedJobId) setSelectedJobId(initialSelectedJobId)
  }, [initialSelectedJobId])

  const selectedJob = jobs.find((job) => job.id === selectedJobId)
  const getOrgType = (orgId?: string | null) => organizations.find((org) => org.id === orgId)?.org_type || ''
  const companyOrgs = useMemo(() => organizations.filter((org) => org.org_type === 'company'), [organizations])
  const agencyOrgs = useMemo(() => organizations.filter((org) => org.org_type === 'agency'), [organizations])
  const postWorkLocations = useMemo(() => {
    const byId = new Map(validLocations.map((location) => [location.id, location]))
    if (getOrgType(selectedOrgId) !== 'agency') return [...byId.values()]
    relationshipAccess.forEach((entry) => {
      ;(entry.grantedLocations || [])
        .filter((location) => location.agencyOrgId === selectedOrgId && location.canCreateJobs && location.canCreateShifts)
        .forEach((location) => byId.set(location.locationId, {
          id: location.locationId,
          tenant_id: location.locationTenantId,
          locationTenantId: location.locationTenantId,
          location_key: location.locationKey || undefined,
          name: location.name,
          timezone: location.timezone || null,
          ownerOrgId: location.companyOrgId || location.ownerOrgId || null,
          ownerOrgType: location.ownerOrgType || 'company',
          companyOrgId: location.companyOrgId || null,
          status: 'active',
        }))
    })
    return [...byId.values()]
  }, [organizations, relationshipAccess, selectedOrgId, validLocations])

  const delegatedRelationshipEntries = useMemo(() => (
    relationshipAccess.filter((entry) => (
      entry.permissions?.canCreateJobsOnBehalf && !['revoked', 'suspended'].includes(entry.status || '')
    ))
  ), [relationshipAccess])

  const selectedDelegatedEntry = useMemo(() => (
    jobDelegatedRelId ? delegatedRelationshipEntries.find((entry) => entry.relationshipId === jobDelegatedRelId) || null : null
  ), [jobDelegatedRelId, delegatedRelationshipEntries])

  const delegatedGrantedLocations: GrantedClientLocation[] = useMemo(() => (
    selectedDelegatedEntry?.grantedLocations?.filter((loc) => loc.canCreateJobs) || []
  ), [selectedDelegatedEntry])

  const relatedCompanyIdsByAgency = useMemo(() => {
    const map = new Map<string, Set<string>>()
    organizationRelationships.forEach((relationship) => {
      if (relationship.status !== 'active') return
      const sourceType = relationship.source_org_type || getOrgType(relationship.source_org_id)
      const targetType = relationship.target_org_type || getOrgType(relationship.target_org_id)
      if (sourceType === 'agency' && targetType === 'company') {
        map.set(relationship.source_org_id, (map.get(relationship.source_org_id) || new Set()).add(relationship.target_org_id))
      }
      if (targetType === 'agency' && sourceType === 'company') {
        map.set(relationship.target_org_id, (map.get(relationship.target_org_id) || new Set()).add(relationship.source_org_id))
      }
    })
    return map
  }, [organizationRelationships, organizations])
  const locationBelongsToOrg = (location: AccessibleLocation, orgId?: string | null) => {
    if (!orgId) return false
    const metadata = location.metadata || {}
    const orgIds = [
      location.ownerOrgId,
      location.owner_org_id,
      location.companyOrgId,
      location.company_org_id,
      metadata.ownerOrgId,
      metadata.owner_org_id,
      metadata.companyOrgId,
      metadata.company_org_id,
      metadata.linkedCompanyOrgId,
      metadata.linked_company_org_id,
    ].filter(Boolean).map(String)
    return orgIds.includes(orgId)
  }
  const locationMatchesSelectedJobContext = (location: AccessibleLocation, form = jobForm) => {
    const createdType = getOrgType(form.createdByOrgId)
    if (form.companyOrgId) return locationBelongsToOrg(location, form.companyOrgId)
    if (createdType === 'company') return locationBelongsToOrg(location, form.createdByOrgId)
    if (createdType === 'agency' || form.managingAgencyOrgId) {
      const agencyId = form.managingAgencyOrgId || form.createdByOrgId
      const relatedCompanyIds = Array.from(relatedCompanyIdsByAgency.get(agencyId) || [])
      if (relatedCompanyIds.length) return relatedCompanyIds.some((orgId) => locationBelongsToOrg(location, orgId)) || locationBelongsToOrg(location, agencyId)
    }
    return true
  }
  const locationMatchesSelectedShiftContext = (location: AccessibleLocation, job = selectedJob) => {
    if (!job) return false
    if (job.company_org_id) return locationBelongsToOrg(location, job.company_org_id)
    return [job.created_by_org_id, job.managing_agency_org_id].filter(Boolean).some((orgId) => locationBelongsToOrg(location, orgId))
  }
  const groupLocations = (locations: AccessibleLocation[], preferredLocationId?: string | null) => {
    const groups = new Map<string, AccessibleLocation[]>()
    locations.forEach((location) => {
      const ownerOrgId = getLocationOrgId(location)
      const owner = organizations.find((org) => org.id === ownerOrgId)
      const label = preferredLocationId && location.id === preferredLocationId
        ? t('Job location')
        : owner?.org_type === 'agency'
          ? t('Agency locations')
          : owner?.name
            ? t('{name} locations', { name: owner.name })
            : t('Other accessible locations')
      groups.set(label, [...(groups.get(label) || []), location])
    })
    return Array.from(groups.entries())
  }
  const jobLocationOptions = useMemo(() => validLocations.filter((location) => locationMatchesSelectedJobContext(location)), [validLocations, jobForm, relatedCompanyIdsByAgency, organizations])
  const shiftLocationOptions = useMemo(() => {
    if (!selectedJob) return []
    return validLocations
      .filter((location) => locationMatchesSelectedShiftContext(location, selectedJob))
      .sort((a, b) => (a.id === selectedJob.location_id ? -1 : b.id === selectedJob.location_id ? 1 : locationLabel(a).localeCompare(locationLabel(b))))
  }, [validLocations, selectedJob, organizations])
  const locationConflict = Boolean(!selectedDelegatedEntry && jobForm.locationId && !jobLocationOptions.some((location) => location.id === jobForm.locationId))
  const workerOptions = useMemo(() => {
    const seen = new Set<string>()
    return workerRelationships.filter((worker): worker is WorkerRelationshipOption => {
      const workerProfileId = worker.worker_profile_id
      if (!workerProfileId || seen.has(workerProfileId)) return false
      seen.add(workerProfileId)
      return true
    })
  }, [workerRelationships])
  const parsedSkillTags = useMemo(() => parseSkillTags(skillTagDraft), [skillTagDraft])
  const pickerSkills = useMemo(() => jobSkillDrafts.map((draft) => draft.skill), [jobSkillDrafts])
  const setPickerSkills = (nextSkills: SkillCatalogResult[]) => {
    setJobSkillDrafts((current) => nextSkills.map((skill) => {
      const existing = current.find((draft) => draft.skill.id === skill.id)
      return existing || { skill, requirementType: 'required', minimumLevel: '' }
    }))
  }
  const jobRequiredSkillsPayload = () => jobSkillDrafts.map((draft) => ({
    skillId: draft.skill.id,
    requirementType: draft.requirementType,
    minimumLevel: draft.minimumLevel || null,
  }))
  const skillDraftFromJob = (job: Job): JobSkillDraft[] => (job.requiredSkillsCanonical || job.required_skills_canonical || []).map((entry) => ({
    skill: {
      id: entry.skillId,
      canonicalName: entry.canonicalName,
      status: 'active',
      matchType: 'suggestion',
      confidence: 1,
      category: entry.category ? { id: entry.category, name: entry.category } : null,
      industries: normalizeSkillIndustries(entry.industries),
    },
    requirementType: entry.requirementType || 'required',
    minimumLevel: (entry.minimumLevel || '') as JobSkillDraft['minimumLevel'],
  }))
  const renderJobSkillChips = (job: Job) => {
    const canonical = job.requiredSkillsCanonical || job.required_skills_canonical || []
    if (canonical.length) {
      return canonical.map((skill) => (
        <span key={`${skill.skillId}:${skill.requirementType}`} className="admin-chip">
          {skill.canonicalName} · {tc(skill.requirementType)}{skill.minimumLevel ? ` · ${tc(skill.minimumLevel)}` : ''}
        </span>
      ))
    }
    return (job.required_skill_tags || []).map((tag) => <span key={tag} className="admin-chip">{t('Legacy skill')} · {tag}</span>)
  }
  const selectedMatchShift = useMemo(() => shifts.find((shift) => shift.id === selectedMatchShiftId) || shifts[0], [selectedMatchShiftId, shifts])
  const visibleShifts = useMemo(() => shifts.filter((shift) => {
    const lifecycleStatus = shiftLifecycleStatus(shift)
    if (detailLifecycle === 'active') return !isHistoricalLifecycle(lifecycleStatus)
    if (detailLifecycle === 'history') return isHistoricalLifecycle(lifecycleStatus)
    return true
  }), [shifts, detailLifecycle])
  const visibleAssignments = useMemo(() => assignments.filter((assignment) => {
    const lifecycleStatus = assignmentLifecycleStatus(assignment)
    if (detailLifecycle === 'active') return !isHistoricalLifecycle(lifecycleStatus)
    if (detailLifecycle === 'history') return isHistoricalLifecycle(lifecycleStatus)
    return true
  }), [assignments, detailLifecycle])
  const filteredShiftMatches = useMemo(() => {
    const term = matchSearch.trim().toLowerCase()
    if (!term) return shiftMatches
    return shiftMatches.filter((match) => [
      matchWorkerLabel(match),
      match.current_status,
      match.application_status,
      match.assignment_status,
      ...(match.skill_names || []),
      ...(match.matched_skill_names || []),
      ...(match.missing_skill_names || []),
      ...(match.organization_names || []),
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)))
  }, [matchSearch, shiftMatches])

  const refreshRankedApplicants = async (shiftId = selectedMatchShift?.id) => {
    if (!shiftId) {
      setRankedApplicants([])
      return
    }
    setRankedApplicants(await listRankedShiftApplicants(shiftId))
  }
  const visibleJobs = jobList.visibleItems
  const jobSummary = useMemo(() => ({
    total: visibleJobs.length,
    drafts: visibleJobs.filter((job) => jobLifecycleStatus(job) === 'draft').length,
    open: visibleJobs.filter((job) => ['posted', 'open', 'partially_filled', 'in_progress'].includes(jobLifecycleStatus(job))).length,
    staffed: visibleJobs.filter((job) => Number(job.shift_count || 0) > 0 && Number(job.open_shift_count || 0) === 0).length,
    atRisk: visibleJobs.filter((job) => Number(job.open_shift_count || 0) > 0 || Number(job.pending_applications_count || 0) > 0 || Number(job.pending_assignments_count || 0) > 0).length,
  }), [visibleJobs])
  const brandForOrg = (orgId?: string | null, fallbackName?: string | null) => {
    const brand = findBrand(orgId, organizations, brandingRows)
    return brand.organization || brand.branding
      ? brand
      : { ...brand, fallbackName: fallbackName || brand.fallbackName }
  }

  const jobCompanyBrand = (job: Job) => brandForOrg(job.company_org_id, job.company_org_name)
  const jobAgencyBrand = (job: Job) => {
    // Cross-tenant delegated jobs: prefer the agency org that actually created it on behalf of the company
    if (job.created_on_behalf_by_agency_org_id && job.created_on_behalf_by_agency_org_name) {
      return brandForOrg(job.created_on_behalf_by_agency_org_id, job.created_on_behalf_by_agency_org_name)
    }
    return brandForOrg(job.managing_agency_org_id || job.posting_agency_org_id || job.created_by_org_id, job.managing_agency_org_name || job.posting_agency_name || job.created_by_org_name)
  }

  useEffect(() => {
    setJobForm((current) => ({ ...current, createdByOrgId: selectedOrgId || current.createdByOrgId }))
    setAssignmentForm((current) => ({ ...current, assignedByOrgId: selectedOrgId || current.assignedByOrgId }))
  }, [selectedOrgId])

  useEffect(() => {
    setJobForm((current) => {
      if (!current.locationId) return current
      const location = validLocations.find((row) => row.id === current.locationId)
      return location && locationMatchesSelectedJobContext(location, current) ? current : { ...current, locationId: '' }
    })
  }, [jobForm.companyOrgId, jobForm.createdByOrgId, jobForm.managingAgencyOrgId, validLocations])

  useEffect(() => {
    if (selectedJob && !shiftForm.locationId && selectedJob.location_id) {
      setShiftForm((current) => ({ ...current, locationId: selectedJob.location_id || '' }))
    }
  }, [selectedJobId, selectedJob?.location_id])

  const refreshJobs = async () => {
    if (!canViewJobs) return
    await jobList.refresh()
  }

  const refreshSelectedJob = async (jobId = selectedJobId) => {
    if (!jobId || !canViewJobs) {
      setShifts([])
      setVisibilityRules([])
      setApplications([])
      setAssignments([])
      return
    }
    const [shiftRows, visibilityRows, applicationRows, assignmentRows] = await Promise.all([
      listJobShifts(jobId),
      listJobVisibilityRules(jobId),
      canManageApplications ? listJobApplications(jobId) : Promise.resolve([]),
      canManageAssignments ? listJobAssignments({ jobId }) : Promise.resolve([]),
    ])
    setShifts(shiftRows)
    setVisibilityRules(visibilityRows)
    setApplications(applicationRows)
    setAssignments(assignmentRows)
    setShiftDrafts(Object.fromEntries(shiftRows.map((shift) => [shift.id, toShiftFormDraft(shift)])))
    const matchShiftId = shiftRows.some((shift) => shift.id === selectedMatchShiftId) ? selectedMatchShiftId : shiftRows[0]?.id || ''
    setSelectedMatchShiftId(matchShiftId)
    const [matchRows, rankedRows] = matchShiftId
      ? await Promise.all([listJobShiftMatches(matchShiftId), listRankedShiftApplicants(matchShiftId).catch(() => [])])
      : [[], []]
    setShiftMatches(matchRows)
    setRankedApplicants(rankedRows)
    setAssignmentStatusDrafts(Object.fromEntries(assignmentRows.map((assignment) => [assignment.id, assignment.status])))
    setAssignmentRateDrafts(Object.fromEntries(assignmentRows.map((assignment) => [assignment.id, {
      payRate: rateValue(assignment.pay_rate),
      billRate: rateValue(assignment.bill_rate),
      rateCurrency: assignment.rate_currency || assignment.currency_code || assignment.resolved_currency_code || 'CAD',
    }])))
  }

  const refreshShiftMatches = async (shiftId = selectedMatchShift?.id || '') => {
    if (!shiftId) {
      setShiftMatches([])
      setRankedApplicants([])
      return
    }
    const [matchRows, rankedRows] = await Promise.all([listJobShiftMatches(shiftId), listRankedShiftApplicants(shiftId).catch(() => [])])
    setShiftMatches(matchRows)
    setRankedApplicants(rankedRows)
  }

  useEffect(() => {
    if (!canViewJobs) {
      return
    }
    let mounted = true
    setError('')
    listSkills()
      .then((skillRows) => {
        if (!mounted) return
        setSkills(skillRows)
      })
      .catch((err: any) => mounted && setError(apiErrorMessage(err, t('Unable to load jobs.'))))
    return () => { mounted = false }
  }, [canViewJobs])

  useEffect(() => {
    if (!selectedJobId && jobList.items[0]) setSelectedJobId(jobList.items[0].id)
  }, [jobList.items, selectedJobId])

  useEffect(() => {
    refreshSelectedJob().catch((err: any) => setError(apiErrorMessage(err, t('Unable to load selected job details.'))))
  }, [selectedJobId, canManageApplications, canManageAssignments])

  useEffect(() => {
    if (selectedJob) {
      setEditTitle(selectedJob.title)
      setEditDescription(selectedJob.description || '')
      setStatusDraft(selectedJob.status)
    }
  }, [selectedJobId])

  const runAction = async (action: () => Promise<void>, success: string) => {
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await action()
      setMessage(success)
    } catch (err: any) {
      setError(apiErrorMessage(err, t('Jobs action failed.')))
    } finally {
      setSaving(false)
    }
  }

  const requestLifecycleReason = (label: string, purge = false) => {
    const reason = window.prompt(`${label}\n\n${t('Reason is required.')}${purge ? `\n${t('Type a reason for irreversible demo/test cleanup.')}` : ''}`)
    return reason?.trim() || ''
  }

  const dependencySummary = (dependencies: Array<{ type: string; count: number }>) => (
    dependencies.map((dependency) => `${dependency.type}: ${dependency.count}`).join(', ') || 'none'
  )

  const runOperationalLifecycle = async (
    label: string,
    action: (reason: string) => Promise<unknown>,
    success: string,
    options: { destructive?: boolean; purge?: boolean; refresh?: () => Promise<void> } = {},
  ) => {
    const reason = requestLifecycleReason(label, options.purge)
    if (!reason) return
    if (options.destructive && !window.confirm(`${label}\n\n${options.purge ? t('Purge demo data is irreversible and only for demo/test records.') : t('This action may remove data if safe.')}`)) return
    await runAction(async () => {
      await action(reason)
      await (options.refresh || (async () => { await refreshSelectedJob(); await refreshJobs() }))()
    }, success)
  }

  const runDeleteWithImpact = async (
    label: string,
    getImpact: () => Promise<{ canDelete: boolean; recommendedAction: string; dependencies: Array<{ type: string; count: number }> }>,
    action: (reason: string) => Promise<unknown>,
    success: string,
  ) => {
    const reason = requestLifecycleReason(`${label}\n\n${t('Delete removes only records with no historical dependencies.')}`)
    if (!reason) return
    await runAction(async () => {
      const impact = await getImpact()
      if (!impact.canDelete) {
        throw new Error(t('Delete blocked. Dependencies: {dependencies}. Recommended action: {action}.', { dependencies: dependencySummary(impact.dependencies), action: impact.recommendedAction }))
      }
      if (!window.confirm(`${label}\n\n${t('No dependencies were found. Delete this empty record?')}`)) return
      await action(reason)
      await refreshSelectedJob()
      await refreshJobs()
    }, success)
  }

  const handleCreateJob = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageJobs) return
    if (!jobForm.createdByOrgId) {
      setError(t('Posting organization is required.'))
      return
    }
    if (!jobForm.companyOrgId) {
      setError(t('Client/company is required before scheduling shifts.'))
      return
    }
    if (!jobForm.locationId) {
      setError(t('Location is required before scheduling shifts.'))
      return
    }
    if (!jobForm.title.trim()) {
      setError(t('Job title is required.'))
      return
    }
    if (canViewPayRate && !validRate(jobForm.payRate)) {
      setError(t('Pay rate must be a valid number.'))
      return
    }
    if (canViewBillRate && !validRate(jobForm.billRate)) {
      setError(t('Bill rate must be a valid number.'))
      return
    }
    if ((canViewPayRate || canViewBillRate) && !/^[A-Z]{3}$/.test(currencyValue(jobForm.currencyCode))) {
      setError(t('Currency must be a 3-letter code such as CAD or USD.'))
      return
    }
    if (locationConflict) {
      setError(t('Selected location does not belong to the selected company or agency context.'))
      return
    }
    await runAction(async () => {
      const job = await createJob({
        ...jobForm,
        companyOrgId: jobForm.companyOrgId || null,
        managingAgencyOrgId: jobForm.managingAgencyOrgId || null,
        locationId: jobForm.locationId || null,
        matchingRadiusKm: jobForm.workMode === 'remote' ? null : Number(jobForm.matchingRadiusKm || 25),
        ...(canViewPayRate ? { payRate: rateNumber(jobForm.payRate ?? '') } : {}),
        ...(canViewBillRate ? { billRate: rateNumber(jobForm.billRate ?? '') } : {}),
        ...((canViewPayRate || canViewBillRate) ? { currencyCode: currencyValue(jobForm.currencyCode) } : {}),
        requiredSkills: jobRequiredSkillsPayload(),
        requiredSkillTags: parsedSkillTags,
      })
      await refreshJobs()
      setSelectedJobId(job.id)
      const start = new Date()
      start.setMinutes(0, 0, 0)
      start.setHours(start.getHours() + 1)
      const end = new Date(start.getTime() + 8 * 60 * 60 * 1000)
      setShiftForm({
        locationId: job.location_id || '',
        startsAt: toInputDateTime(start.toISOString()),
        endsAt: toInputDateTime(end.toISOString()),
        workersNeeded: 1,
        status: 'open',
        workMode: '',
        matchingRadiusKm: '',
        payRate: '',
        billRate: '',
        currencyCode: '',
      })
      setJobForm({ ...jobForm, title: '', description: '', jobType: '', status: 'draft', visibilityType: 'private', approvalRequired: false, workMode: 'onsite', matchingRadiusKm: 25, requiredSkillTags: [] })
      setJobSkillDrafts([])
      setSkillTagDraft('')
      setShowJobModal(false)
    }, t('Job/order created.'))
  }

  const handleUpdateJob = async (event: React.FormEvent) => {
    event.preventDefault()
    const targetJob = editingJob || selectedJob
    if (!targetJob || !canManageJobs) return
    await runAction(async () => {
      await updateJob(targetJob.id, {
        title: editTitle,
        description: editDescription || null,
        workMode: jobForm.workMode || 'onsite',
        matchingRadiusKm: jobForm.workMode === 'remote' ? null : Number(jobForm.matchingRadiusKm || 25),
        ...(canViewPayRate ? { payRate: rateNumber(jobForm.payRate ?? '') } : {}),
        ...(canViewBillRate ? { billRate: rateNumber(jobForm.billRate ?? '') } : {}),
        ...((canViewPayRate || canViewBillRate) ? { currencyCode: currencyValue(jobForm.currencyCode) } : {}),
        requiredSkills: jobRequiredSkillsPayload(),
        requiredSkillTags: parsedSkillTags,
      })
      await updateJobStatus(targetJob.id, { status: statusDraft })
      await refreshJobs()
      setShowJobModal(false)
      setEditingJob(null)
    }, t('Job basics updated.'))
  }

  const handleCreateShift = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedJob || !canManageJobs) return
    if (shiftForm.locationId && !shiftLocationOptions.some((location) => location.id === shiftForm.locationId)) {
      setError(t('Selected shift location does not belong to this job company or agency context.'))
      return
    }
    if (!shiftForm.startsAt) {
      setError(t('Shift start time is required.'))
      return
    }
    if (!shiftForm.endsAt) {
      setError(t('Shift end time is required.'))
      return
    }
    if (!positiveInteger(shiftForm.workersNeeded)) {
      setError(t('Required headcount must be greater than 0.'))
      return
    }
    if (canViewPayRate && !validRate(shiftForm.payRate)) {
      setError(t('Shift pay override must be a valid number.'))
      return
    }
    if (canViewBillRate && !validRate(shiftForm.billRate)) {
      setError(t('Shift bill override must be a valid number.'))
      return
    }
    if ((canViewPayRate || canViewBillRate) && shiftForm.currencyCode && !/^[A-Z]{3}$/.test(currencyValue(shiftForm.currencyCode))) {
      setError(t('Shift currency override must be a 3-letter code such as CAD or USD.'))
      return
    }
    if (new Date(shiftForm.endsAt).getTime() <= new Date(shiftForm.startsAt).getTime()) {
      setError(t('Shift end time must be after the start time.'))
      return
    }
    await runAction(async () => {
      const start = new Date(shiftForm.startsAt)
      const end = new Date(shiftForm.endsAt)
      for (let index = 0; index < repeatShiftCount; index += 1) {
        const offset = index * 24 * 60 * 60 * 1000
        await createJobShift(selectedJob.id, {
          locationId: shiftForm.locationId || null,
          startsAt: new Date(start.getTime() + offset).toISOString(),
          endsAt: new Date(end.getTime() + offset).toISOString(),
          workersNeeded: Number(shiftForm.workersNeeded),
          status: shiftForm.status,
          workMode: shiftForm.workMode || null,
          matchingRadiusKm: shiftForm.matchingRadiusKm === '' ? null : Number(shiftForm.matchingRadiusKm),
          ...(canViewPayRate ? { payRate: rateNumber(shiftForm.payRate) } : {}),
          ...(canViewBillRate ? { billRate: rateNumber(shiftForm.billRate) } : {}),
          ...((canViewPayRate || canViewBillRate) ? { currencyCode: shiftForm.currencyCode ? currencyValue(shiftForm.currencyCode) : null } : {}),
          metadata: repeatShiftCount > 1 ? { generatedByUiPattern: 'daily', generatedIndex: index + 1, generatedCount: repeatShiftCount } : null,
        })
      }
      await refreshSelectedJob()
      setShiftForm({ locationId: selectedJob.location_id || '', startsAt: '', endsAt: '', workersNeeded: 1, status: 'open', workMode: '', matchingRadiusKm: '', payRate: '', billRate: '', currencyCode: '' })
      setRepeatShiftCount(1)
      setShowShiftModal(false)
    }, repeatShiftCount > 1 ? t('{count} shifts added.', { count: repeatShiftCount }) : t('Shift added.'))
  }

  const handleShiftModalSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (editingShift) {
      setShiftDrafts((current) => ({
        ...current,
        [editingShift.id]: {
          startsAt: shiftForm.startsAt,
          endsAt: shiftForm.endsAt,
          workersNeeded: shiftForm.workersNeeded,
          status: shiftForm.status,
          workMode: shiftForm.workMode,
          matchingRadiusKm: shiftForm.matchingRadiusKm,
          payRate: shiftForm.payRate,
          billRate: shiftForm.billRate,
          currencyCode: shiftForm.currencyCode,
        },
      }))
      if (!canManageJobs) return
      if (!positiveInteger(shiftForm.workersNeeded)) {
        setError(t('Required headcount must be greater than 0.'))
        return
      }
      if ((canViewPayRate && !validRate(shiftForm.payRate)) || (canViewBillRate && !validRate(shiftForm.billRate))) {
        setError(t('Shift rate overrides must be valid numbers.'))
        return
      }
      if ((canViewPayRate || canViewBillRate) && shiftForm.currencyCode && !/^[A-Z]{3}$/.test(currencyValue(shiftForm.currencyCode))) {
        setError(t('Shift currency override must be a 3-letter code such as CAD or USD.'))
        return
      }
      if (!shiftForm.startsAt || !shiftForm.endsAt || new Date(shiftForm.endsAt).getTime() <= new Date(shiftForm.startsAt).getTime()) {
        setError(t('Shift end time must be after the start time.'))
        return
      }
      await runAction(async () => {
        await updateJobShift(editingShift.id, {
          locationId: shiftForm.locationId || null,
          startsAt: toPayloadDateTime(shiftForm.startsAt),
          endsAt: toPayloadDateTime(shiftForm.endsAt),
          workersNeeded: shiftForm.workersNeeded,
          status: shiftForm.status,
          workMode: shiftForm.workMode || null,
          matchingRadiusKm: shiftForm.matchingRadiusKm === '' ? null : Number(shiftForm.matchingRadiusKm),
          ...(canViewPayRate ? { payRate: rateNumber(shiftForm.payRate) } : {}),
          ...(canViewBillRate ? { billRate: rateNumber(shiftForm.billRate) } : {}),
          ...((canViewPayRate || canViewBillRate) ? { currencyCode: shiftForm.currencyCode ? currencyValue(shiftForm.currencyCode) : null } : {}),
        })
        await refreshSelectedJob()
        setShowShiftModal(false)
        setEditingShift(null)
      }, t('Shift updated.'))
      return
    }
    await handleCreateShift(event)
  }

  const handleDuplicateShift = async (shift: JobShift) => {
    if (!canManageJobs) return
    await runAction(async () => {
      await duplicateJobShift(shift.id)
      await refreshSelectedJob()
      await refreshJobs()
    }, t('Shift duplicated.'))
  }

  const handleCancelShift = async (shift: JobShift) => {
    if (!canManageJobs) return
    await runAction(async () => {
      await cancelJobShift(shift.id)
      await refreshSelectedJob()
      await refreshJobs()
    }, t('Shift cancelled.'))
  }

  const handlePublishShift = async (shift: JobShift) => {
    if (!canManageJobs) return
    await runAction(async () => {
      await publishJobShift(shift.id, { visibility: 'public_marketplace', maxWorkers: shift.max_workers || shift.workers_needed })
      await refreshSelectedJob()
      await refreshJobs()
    }, t('Shift published.'))
  }

  const handleCreateVisibility = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedJob || !canManageJobs) return
    await runAction(async () => {
      await createJobVisibilityRule(selectedJob.id, {
        visibilityScope: visibilityForm.visibilityScope,
        visibleToOrgId: visibilityForm.visibilityScope === 'specific_worker' || visibilityForm.visibilityScope === 'public_marketplace' ? null : visibilityForm.visibleToOrgId || null,
        visibleToWorkerProfileId: visibilityForm.visibilityScope === 'specific_worker' ? visibilityForm.visibleToWorkerProfileId || null : null,
        canApply: visibilityForm.canApply,
        canAssign: visibilityForm.canAssign,
        canViewRates: visibilityForm.canViewRates,
      })
      await refreshSelectedJob()
      setShowVisibilityModal(false)
    }, t('Visibility rule added.'))
  }

  const handleCreateApplication = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedJob) return
    await runAction(async () => {
      await createJobApplication(selectedJob.id, {
        workerProfileId: applicationForm.workerProfileId,
        jobShiftId: applicationForm.jobShiftId || null,
        applyingThroughOrgId: applicationForm.applyingThroughOrgId || null,
        coverNote: applicationForm.coverNote || null,
      })
      await refreshSelectedJob()
      setApplicationForm({ workerProfileId: '', jobShiftId: '', applyingThroughOrgId: '', coverNote: '' })
      setShowApplicationModal(false)
    }, t('Application created.'))
  }

  const handleCreateApplicationFromMatch = async (match: JobShiftWorkerMatch) => {
    if (!selectedJob || !selectedMatchShift || !canManageApplications || match.application_id || !matchIsEligible(match)) return
    await runAction(async () => {
      await createJobApplication(selectedJob.id, {
        workerProfileId: match.worker_profile_id,
        jobShiftId: selectedMatchShift.id,
        coverNote: null,
        matchScore: match.match_score === null || match.match_score === undefined ? null : Number(match.match_score),
      })
      await refreshSelectedJob()
      await refreshShiftMatches(selectedMatchShift.id)
    }, t('Application created from match.'))
  }

  const handleCreateAssignmentFromMatch = async (match: JobShiftWorkerMatch) => {
    if (!selectedJob || !selectedMatchShift || !canManageAssignments || matchAssignmentBlockReason(match, selectedMatchShift)) return
    await runAction(async () => {
      await createJobAssignment({
        jobId: selectedJob.id,
        jobShiftId: selectedMatchShift.id,
        workerProfileId: match.worker_profile_id,
        applicationId: match.application_id || null,
        assignedByOrgId: selectedOrgId || null,
        supplyingAgencyOrgId: selectedJob.managing_agency_org_id || null,
        companyOrgId: selectedJob.company_org_id || null,
        status: selectedJob.approval_required ? 'pending_company_approval' : 'confirmed',
      })
      await refreshSelectedJob()
      await refreshShiftMatches(selectedMatchShift.id)
    }, t('Worker assignment created.'))
  }

  const handleCreateAssignment = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedJob || !canManageAssignments) return
    if (!assignmentForm.workerProfileId) {
      setError(t('Select a worker before creating an assignment.'))
      return
    }
    if (!assignmentForm.jobShiftId) {
      setError(t('Select a shift before creating an assignment.'))
      return
    }
    const targetShift = shifts.find((shift) => shift.id === assignmentForm.jobShiftId)
    if (!targetShift || isHistoricalLifecycle(shiftLifecycleStatus(targetShift))) {
      setError(t('Cancelled, completed, or expired shifts cannot receive new assignments.'))
      return
    }
    if ((canViewPayRate && !validRate(assignmentForm.payRate)) || (canViewBillRate && !validRate(assignmentForm.billRate))) {
      setError(t('Assignment rate overrides must be valid numbers.'))
      return
    }
    if ((canViewPayRate || canViewBillRate) && !/^[A-Z]{3}$/.test(currencyValue(assignmentForm.rateCurrency))) {
      setError(t('Assignment currency must be a 3-letter code such as CAD or USD.'))
      return
    }
    await runAction(async () => {
      await createJobAssignment({
        jobId: selectedJob.id,
        jobShiftId: assignmentForm.jobShiftId,
        workerProfileId: assignmentForm.workerProfileId,
        applicationId: assignmentForm.applicationId || null,
        assignedByOrgId: assignmentForm.assignedByOrgId || null,
        supplyingAgencyOrgId: assignmentForm.supplyingAgencyOrgId || null,
        companyOrgId: assignmentForm.companyOrgId || selectedJob.company_org_id || null,
        status: assignmentForm.status,
        ...(canViewPayRate ? { payRate: assignmentForm.payRate ? Number(assignmentForm.payRate) : null } : {}),
        ...(canViewBillRate ? { billRate: assignmentForm.billRate ? Number(assignmentForm.billRate) : null } : {}),
        ...((canViewPayRate || canViewBillRate) ? { rateCurrency: assignmentForm.rateCurrency || 'CAD' } : {}),
      })
      await refreshSelectedJob()
      setAssignmentForm({ ...assignmentForm, workerProfileId: '', applicationId: '', payRate: '', billRate: '' })
      setShowAssignmentModal(false)
    }, t('Assignment created.'))
  }

  const handleCreateSkill = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageSkills) return
    await runAction(async () => {
      await createSkill({ name: skillForm.name, category: skillForm.category || null, description: skillForm.description || null })
      setSkills(await listSkills())
      setSkillForm({ name: '', category: '', description: '' })
      setShowSkillModal(false)
    }, t('Skill created.'))
  }

  const openCreateJob = () => {
    setEditingJob(null)
    setJobDelegatedRelId('')
    setJobForm((current) => ({
      ...current,
      createdByOrgId: selectedOrgId || current.createdByOrgId,
      companyOrgId: '',
      managingAgencyOrgId: '',
      locationId: '',
      locationTenantId: null,
      title: '',
      description: '',
      jobType: '',
      status: 'draft',
      visibilityType: 'private',
      approvalRequired: false,
      workMode: 'onsite',
      matchingRadiusKm: 25,
      payRate: '',
      billRate: '',
      currencyCode: 'CAD',
      requiredSkillTags: [],
    }))
    setJobSkillDrafts([])
    setSkillTagDraft('')
    setShowJobModal(true)
  }

  const openCreateJobForLocation = (draft: LocationJobDraft) => {
    const location = validLocations.find((row) => row.id === draft.locationId)
    if (!location || !canManageJobs) {
      onConsumeInitialJobDraft?.()
      return
    }

    const ownerOrgId = draft.ownerOrgId || getLocationOrgId(location)
    const ownerType = getOrgType(ownerOrgId)
    const createdByOrgId = ownerOrgId || selectedOrgId || jobForm.createdByOrgId
    setEditingJob(null)
    setSelectedJobId('')
    setShowJobDrawer(false)
    setJobForm((current) => ({
      ...current,
      createdByOrgId,
      companyOrgId: ownerType === 'company' ? ownerOrgId : '',
      managingAgencyOrgId: ownerType === 'agency' ? ownerOrgId : current.managingAgencyOrgId || '',
      locationId: location.id,
      title: '',
      description: '',
      jobType: '',
      status: 'draft',
      visibilityType: 'private',
      approvalRequired: false,
      workMode: 'onsite',
      matchingRadiusKm: 25,
      payRate: '',
      billRate: '',
      currencyCode: 'CAD',
      requiredSkillTags: [],
    }))
    setJobSkillDrafts([])
    setShiftForm((current) => ({ ...current, locationId: location.id }))
    setSkillTagDraft('')
    setMessage(t('{location} selected for the new job/order draft.', { location: locationLabel(location) }))
    setShowJobModal(true)
    onConsumeInitialJobDraft?.()
  }

  useEffect(() => {
    if (!initialJobDraft) return
    openCreateJobForLocation(initialJobDraft)
  }, [initialJobDraft?.nonce, canManageJobs, validLocations])

  useEffect(() => {
    if (!delegatedJobContext) return
    const entry = delegatedRelationshipEntries.find((e) => e.relationshipId === delegatedJobContext.relationshipId)
    if (!entry || !canManageJobs) {
      onConsumeDelegatedJobContext?.()
      return
    }
    setJobDelegatedRelId(delegatedJobContext.relationshipId)
    setEditingJob(null)
    setSelectedJobId('')
    setShowJobDrawer(false)
    setJobForm((current) => ({
      ...current,
      createdByOrgId: delegatedJobContext.agencyOrgId || selectedOrgId || current.createdByOrgId,
      companyOrgId: delegatedJobContext.companyOrgId || '',
      managingAgencyOrgId: delegatedJobContext.agencyOrgId || '',
      locationId: delegatedJobContext.locationId || '',
      locationTenantId: delegatedJobContext.locationTenantId || null,
      title: '',
      description: '',
      jobType: '',
      status: 'draft',
      visibilityType: 'private',
      approvalRequired: false,
      workMode: 'onsite',
      matchingRadiusKm: 25,
      payRate: '',
      billRate: '',
      currencyCode: 'CAD',
      requiredSkillTags: [],
    }))
    setJobSkillDrafts([])
    setSkillTagDraft('')
    setShowJobModal(true)
    onConsumeDelegatedJobContext?.()
  }, [delegatedJobContext?.nonce])

  const openEditJob = (job: Job) => {
    setSelectedJobId(job.id)
    setEditingJob(job)
    setEditTitle(job.title)
    setEditDescription(job.description || '')
    setStatusDraft(job.status)
    setJobForm((current) => ({
      ...current,
      title: job.title,
      description: job.description || '',
      workMode: job.work_mode || 'onsite',
      matchingRadiusKm: Number(job.matching_radius_km ?? 25),
      payRate: rateValue(job.pay_rate),
      billRate: rateValue(job.bill_rate),
      currencyCode: job.currency_code || 'CAD',
      requiredSkillTags: job.required_skill_tags || [],
    }))
    setJobSkillDrafts(skillDraftFromJob(job))
    setSkillTagDraft((job.required_skill_tags || []).join(', '))
    setShowJobModal(true)
  }

  const openJobDetails = (job: Job) => {
    setSelectedJobId(job.id)
    setShowJobDrawer(true)
  }

  const openCreateShift = (job = selectedJob) => {
    if (!job) return
    setEditingShift(null)
    const lastShift = [...shifts].sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime())[0]
    const nextStart = lastShift?.starts_at ? new Date(new Date(lastShift.starts_at).getTime() + 24 * 60 * 60 * 1000) : null
    const nextEnd = lastShift?.ends_at ? new Date(new Date(lastShift.ends_at).getTime() + 24 * 60 * 60 * 1000) : null
    setRepeatShiftCount(1)
    setShiftForm({
      locationId: lastShift?.location_id || job.location_id || '',
      startsAt: nextStart && Number.isFinite(nextStart.getTime()) ? toInputDateTime(nextStart.toISOString()) : '',
      endsAt: nextEnd && Number.isFinite(nextEnd.getTime()) ? toInputDateTime(nextEnd.toISOString()) : '',
      workersNeeded: lastShift?.workers_needed || 1,
      status: 'open',
      workMode: lastShift?.work_mode || '',
      matchingRadiusKm: lastShift?.matching_radius_km ?? '',
      payRate: rateValue(lastShift?.pay_rate),
      billRate: rateValue(lastShift?.bill_rate),
      currencyCode: lastShift?.currency_code || '',
    })
    setShowShiftModal(true)
  }

  const openEditShift = (shift: JobShift) => {
    const draft = shiftDrafts[shift.id] || toShiftFormDraft(shift)
    setEditingShift(shift)
    setShiftForm({ locationId: shift.location_id || selectedJob?.location_id || '', ...draft })
    setShowShiftModal(true)
  }

  const selectedJobLocationLabel = selectedJob?.location_id
    ? selectedJob.location_name || locationLabel(locationById.get(selectedJob.location_id || '')) || t('Location selected, name unavailable')
    : t('No location')
  const closePostWorkWizard = () => {
    setShowPostWorkWizard(false)
    window.requestAnimationFrame(() => {
      if (postWorkOpenedFromGlobalRef.current) onPostWorkClosed?.()
      else postWorkTriggerRef.current?.focus()
      postWorkOpenedFromGlobalRef.current = false
    })
  }
  const localizedGroupLocations = (locations: AccessibleLocation[], preferredLocationId?: string | null) => (
    groupLocations(locations, preferredLocationId)
  )

  if (!canViewJobs) {
    return <p className="admin-disabled-callout">{industryLabels.explicitIndustry ? t('{resource} access requires job view or management permission.', { resource: industryTerm('jobs') }) : t('Jobs / Orders access requires job view or management permission.')}</p>
  }

  return (
    <div className="stack" data-testid="admin-jobs-panel">
      <div className="admin-section-header">
        <div>
          <h3>{industryLabels.explicitIndustry ? industryTerm('jobs') : t('Jobs / Postings')}</h3>
          <p className="text-muted">{industryLabels.explicitIndustry ? t('Post {jobs}, add {shifts}, manage visibility, and move applications into {assignments} with names and labels first.', { jobs: industryTermLower('jobs'), shifts: industryTermLower('shifts'), assignments: industryTermLower('assignments') }) : t('Post jobs, add shifts, manage visibility, and move applications into assignments with names and labels first.')}</p>
        </div>
        <div className="inline-group">
          <select aria-label={t('Organization')} className="input" value={selectedOrgId} onChange={(event) => onSelectOrg(event.target.value)}>
            {organizations.map((org) => <option key={org.id} value={org.id}>{organizationLabel(organizations, org.id)}</option>)}
          </select>
          {isAdvanced ? <button type="button" className="btn btn-secondary" onClick={() => setShowShiftImportModal(true)}>{t('Import shifts')}</button> : null}
          <button ref={postWorkTriggerRef} type="button" className="btn btn-primary" disabled={!canManageJobs} onClick={() => { postWorkOpenedFromGlobalRef.current = false; setShowPostWorkWizard(true) }}>+ {t('Post Work')}</button>
          {isAdvanced ? <button type="button" className="btn btn-secondary" disabled={!canManageJobs} onClick={openCreateJob}>{createJobLabel}</button> : null}
          {isAdvanced ? <button type="button" className="btn btn-secondary" disabled={!canManageSkills} onClick={() => setShowSkillModal(true)}>{t('Skills')}</button> : null}
        </div>
      </div>

      {onOpenSchedule ? (
        <div className="inline-group">
          <button type="button" className="btn btn-secondary" onClick={onOpenSchedule}>{t('Open in Schedule')}</button>
        </div>
      ) : null}

      <ErrorBanner message={error || jobList.error} />
      {message ? <p className="success-text">{message}</p> : null}

      <div className="admin-summary-grid jobs-summary-grid" aria-label={t('Jobs and orders summary')}>
        <div className="admin-summary-card"><span>{t('Visible jobs/orders')}</span><strong>{jobSummary.total}</strong></div>
        <div className="admin-summary-card"><span>{t('Draft')}</span><strong>{jobSummary.drafts}</strong></div>
        <div className="admin-summary-card"><span>{t('Open for staffing')}</span><strong>{jobSummary.open}</strong></div>
        <div className="admin-summary-card"><span>{t('Fully staffed')}</span><strong>{jobSummary.staffed}</strong></div>
        <div className="admin-summary-card"><span>{t('At-risk coverage')}</span><strong>{jobSummary.atRisk}</strong></div>
      </div>

      <TableQueryControls table={jobList} ariaLabel={t('Job filters')} searchLabel={t('Search')}
        placeholder={industryLabels.explicitIndustry ? t('{job}, company, {location}, skill', { job: industryTerm('job'), location: industryTermLower('location') }) : t('Job, company, location, skill')}>
        {isAdvanced ? <select className="input" aria-label={t('Status')} value={filters.status} onChange={(event) => jobList.setFilter('status', event.target.value || undefined)}>
          <option value="">{t('All statuses')}</option>
          {jobStatuses.map((status) => <option key={status} value={status}>{tc(status)}</option>)}
        </select> : null}
        <select className="input" aria-label={t('Lifecycle')} value={filters.lifecycle} onChange={(event) => jobList.setFilter('lifecycle', event.target.value)}>
          <option value="active">{industryLabels.explicitIndustry ? t('Active {resources}', { resources: industryTermLower('jobs') }) : t('Active jobs')}</option>
          <option value="history">{t('History')}</option>
          <option value="all">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryTermLower('jobs') }) : t('All jobs')}</option>
        </select>
        {isAdvanced ? <select className="input" aria-label={t('Company')} value={filters.companyOrgId} onChange={(event) => jobList.setFilter('companyOrgId', event.target.value || undefined)}>
          <option value="">{t('All companies')}</option>
          {companyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
        </select> : null}
        {isAdvanced ? <select className="input" aria-label={t('Agency')} value={filters.managingAgencyOrgId} onChange={(event) => jobList.setFilter('managingAgencyOrgId', event.target.value || undefined)}>
          <option value="">{t('All agencies')}</option>
          {agencyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
        </select> : null}
        {isAdvanced ? <select className="input" aria-label={t('Visibility')} value={filters.visibilityType} onChange={(event) => jobList.setFilter('visibilityType', event.target.value || undefined)}>
          <option value="">{t('All visibility')}</option>
          {visibilityTypes.map((type) => <option key={type} value={type}>{tc(type)}</option>)}
        </select> : null}
        <button type="button" className="btn btn-secondary" onClick={() => refreshJobs()} disabled={jobList.loading}>{t('Apply')}</button>
      </TableQueryControls>
      <TableDisplayControls table={jobList} columnsEnabled={isAdvanced} />

      <section className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }} aria-busy={jobList.loading}>
        <AdminTableContainer label={t('Jobs table')} density={jobList.density.density}>
        {jobList.loading && jobs.length === 0 && isSimple ? <LoadingState label={t('Loading jobs...')} /> : (
          isSimple ? (
            <div className="admin-card-grid simple-work-demand-grid" data-testid="simple-work-demand-list">
              {visibleJobs.map((job) => {
                const row = job as any
                const needed = Number(row.required_worker_count || 0)
                const assigned = Number(row.assigned_worker_count || 0)
                const remaining = Math.max(0, needed - assigned)
                const waitingForCompanyApproval = job.status === 'pending_company_approval'
                  || job.approval_status === 'pending_company_approval'
                  || job.delegated_approval_status === 'pending_company_approval'
                const worksite = job.work_mode === 'remote'
                  ? t('Remote')
                  : job.location_name || locationLabel(locationById.get(job.location_id || '')) || t('Worksite not selected')
                return (
                  <article key={job.id} className="admin-summary-card simple-work-card">
                    <div className="admin-panel-toolbar">
                      <div className="stack" style={{ gap: '.2rem' }}>
                        <h4 style={{ margin: 0 }}>{job.title} — {worksite}</h4>
                        <p className="helper">{tc(job.status)} · {needed ? `${assigned} / ${needed} ${t('filled')}` : t('Add a shift to set coverage')}</p>
                      </div>
                      {statusBadge(jobLifecycleStatus(job), tc(job.status), t)}
                    </div>
                    {waitingForCompanyApproval ? (
                      <div className="admin-state admin-state--info">
                        <strong>{t('Waiting for client approval.')}</strong>
                        <p>{t('Workers cannot be assigned until the company approves this work.')}</p>
                      </div>
                    ) : remaining > 0 ? (
                      <div className="admin-state admin-state--warning">
                        <strong>{remaining === 1 ? t('1 worker still needed.') : t('{count} workers still needed.', { count: remaining })}</strong>
                        <button type="button" className="btn btn-primary" disabled={!canManageAssignments} onClick={() => { setSelectedJobId(job.id); setShowJobDrawer(true) }}>{t('Find Workers')}</button>
                      </div>
                    ) : needed > 0 ? <p className="success-text">{t('Coverage is filled.')}</p> : null}
                    <div className="inline-group">
                      <button type="button" className="btn btn-secondary" onClick={() => openJobDetails(job)}>{t('Open work')}</button>
                      <EntityActionMenu>
                        <button type="button" className="btn btn-ghost" disabled={!canManageJobs} onClick={() => openEditJob(job)}>{t('Edit')}</button>
                        <button type="button" className="btn btn-ghost" disabled={!canManageJobs || isHistoricalLifecycle(jobLifecycleStatus(job))} onClick={() => { setSelectedJobId(job.id); setShowJobDrawer(true); openCreateShift(job) }}>{t('Add Shift')}</button>
                        <button type="button" className="btn btn-ghost" disabled={saving || !canManageJobs} onClick={() => runOperationalLifecycle(t('Cancel = stop future use for this work.'), (reason) => cancelJob(job.id, reason), t('Work cancelled.'))}>{t('Cancel')}</button>
                        {canArchiveJobs ? <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Archive = hide this work from normal operations but keep history.'), (reason) => archiveJob(job.id, reason), t('Work archived.'))}>{t('Archive')}</button> : null}
                      </EntityActionMenu>
                    </div>
                  </article>
                )
              })}
              {visibleJobs.length === 0 ? <EmptyState title={t('No work demand found')} description={t('Adjust the search or use Post Work to create your first request.')} action={<button type="button" className="btn btn-primary" onClick={() => setShowPostWorkWizard(true)}>+ {t('Post Work')}</button>} /> : null}
            </div>
          ) : (
          <>
          {jobList.loading && jobs.length === 0 ? <LoadingState label={t('Loading jobs...')} /> : null}
          <table className="workspace-table">
            <thead><tr>
              <th scope="col"><TablePageSelection selection={jobList.selection} /></th>
              <TableSortHeader label={industryLabels.explicitIndustry ? industryTerm('job') : t('Job / order')} sortKey="title" table={jobList} />
              {jobList.preference.isVisible('owner') ? <th scope="col">{t('Customer / owner')}</th> : null}
              {jobList.preference.isVisible('location') ? <th scope="col">{t('Mode and location')}</th> : null}
              <TableSortHeader label={t('Lifecycle')} sortKey="status" table={jobList} />
              {jobList.preference.isVisible('coverage') ? <th scope="col">{t('Coverage')}</th> : null}
              <th scope="col">{t('Actions')}</th></tr></thead>
            <tbody>
              {visibleJobs.map((job) => {
                const openShifts = Number(job.open_shift_count || 0)
                const assignedShifts = Number(job.assigned_shift_count || 0)
                const totalShifts = Number(job.shift_count || 0)
                const coverage = totalShifts === 0 ? t('No shifts scheduled') : openShifts > 0 ? t('At risk') : t('Covered')
                return (
                <tr key={job.id} className={jobList.selection.isSelected(job.id) ? 'is-selected' : undefined}>
                  <td><TableRowSelection selection={jobList.selection} id={job.id} name={job.title} /></td>
                  <td>
                    <button type="button" className="link-button" onClick={() => openJobDetails(job)}>{job.title}</button>
                    <div className="admin-chip-list">{renderJobSkillChips(job)}{!renderJobSkillChips(job).length ? <span className="helper">{job.job_type || t('No required skills listed')}</span> : null}</div>
                  </td>
                  {jobList.preference.isVisible('owner') ? <td>
                    <BrandPair primary={jobCompanyBrand(job)} secondary={jobAgencyBrand(job)} />
                  </td> : null}
                  {jobList.preference.isVisible('location') ? <td>
                    <span className="admin-chip">{tc(job.work_mode || 'onsite')}</span>
                    <div className="helper">{job.work_mode === 'remote' ? t('Remote - no onsite geofence required') : job.location_id ? job.location_name || locationLabel(locationById.get(job.location_id || '')) || t('Location selected, name unavailable') : t('No location')}</div>
                  </td> : null}
                  <td>{statusBadge(jobLifecycleStatus(job), tc(job.status), t)}</td>
                  {jobList.preference.isVisible('coverage') ? <td>
                    <span className={openShifts > 0 ? 'status-pill status-pill--warning' : 'status-pill status-pill--active'}>{coverage}</span>
                    <div className="helper">{t('{total} shifts · {open} open · {assigned} assigned', { total: totalShifts, open: openShifts, assigned: assignedShifts })}</div>
                    <div className="helper">{t('{apps} pending apps · {assignments} pending assignments', { apps: job.pending_applications_count || 0, assignments: job.pending_assignments_count || 0 })}</div>
                  </td> : null}
                  <td>
                    <EntityActionMenu>
                      <button type="button" className="btn btn-ghost" onClick={() => openJobDetails(job)}>{t('View details')}</button>
                      <button type="button" className="btn btn-ghost" disabled={!canManageJobs} title={!canManageJobs ? t('Job management permission is required.') : undefined} onClick={() => openEditJob(job)}>{t('Edit')}</button>
                      <button type="button" className="btn btn-ghost" disabled={!canManageJobs || isHistoricalLifecycle(jobLifecycleStatus(job))} title={!canManageJobs ? t('Job management permission is required.') : isHistoricalLifecycle(jobLifecycleStatus(job)) ? t('Historical jobs cannot receive new shifts.') : undefined} onClick={() => { setSelectedJobId(job.id); setShowJobDrawer(true); openCreateShift(job) }}>{t('Add shift')}</button>
                      <button type="button" className="btn btn-ghost" disabled={!canManageAssignments || isHistoricalLifecycle(jobLifecycleStatus(job))} title={!canManageAssignments ? t('Assignment management permission is required.') : isHistoricalLifecycle(jobLifecycleStatus(job)) ? t('Historical jobs cannot receive new assignments.') : undefined} onClick={() => { setSelectedJobId(job.id); setShowAssignmentModal(true) }}>{assignWorkerLabel}</button>
                      <button type="button" className="btn btn-ghost" disabled={saving || !canManageJobs} onClick={() => runOperationalLifecycle(t('Cancel = stop future use for this job/order.'), (reason) => cancelJob(job.id, reason), t('Job cancelled.'))}>{t('Cancel')}</button>
                      {canArchiveJobs ? <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Archive = hide this job/order from normal operations but keep history.'), (reason) => archiveJob(job.id, reason), t('Job archived.'))}>{t('Archive')}</button> : null}
                      {canRestoreJobs ? <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Restore this job/order to operational use.'), (reason) => restoreJob(job.id, reason), t('Job restored.'))}>{t('Restore')}</button> : null}
                      {canDeleteEmptyJobs ? <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runDeleteWithImpact(t('Delete job/order'), () => getJobDeleteImpact(job.id), (reason) => deleteJob(job.id, reason), t('Job deleted.'))}>{t('Delete')}</button> : null}
                      {lifecyclePermissions?.canPurgeDemoData ? <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Purge demo data = irreversible cleanup for demo/test job records only.'), (reason) => purgeDemoJob(job.id, reason), t('Demo job data purged.'), { destructive: true, purge: true })}>{t('Purge demo data')}</button> : null}
                    </EntityActionMenu>
                  </td>
                </tr>
              )})}
              {!jobList.loading && visibleJobs.length === 0 ? (
                <tr><td colSpan={jobList.preference.visibleCount + 1}><EmptyState title={filters.lifecycle === 'history' ? (industryLabels.explicitIndustry ? t('No historical {resources} found', { resources: industryTermLower('jobs') }) : t('No historical jobs found')) : (industryLabels.explicitIndustry ? t('No active {resources} found', { resources: industryTermLower('jobs') }) : t('No active jobs found'))} description={filters.lifecycle === 'active' ? (industryLabels.explicitIndustry ? t('Completed, cancelled, expired, and unavailable {resources} are hidden from this default view. Switch to History or adjust filters.', { resources: industryTermLower('jobs') }) : t('Completed, cancelled, expired, and unavailable jobs are hidden from this default view. Switch to History or adjust filters.')) : (industryLabels.explicitIndustry ? t('Adjust filters or create a {job} to begin filling {shifts}.', { job: industryTermLower('job'), shifts: industryTermLower('shifts') }) : t('Adjust filters or create a job/order to begin filling shifts.'))} /></td></tr>
              ) : null}
            </tbody>
          </table>
          </>
          )
        )}
        </AdminTableContainer>
        <PaginationControls pagination={jobList.pagination} onPageChange={jobList.setPage} onPageSizeChange={jobList.setPageSize} />
      </section>

      <PostWorkWizard
        isOpen={showPostWorkWizard}
        onClose={closePostWorkWizard}
        organizations={organizations}
        accessibleLocations={postWorkLocations}
        selectedOrgId={selectedOrgId}
        onCreated={(result) => {
          setSelectedJobId(result.job.id)
          setMessage(result.nextAction === 'await_company_approval'
            ? t('Work saved and sent for client approval.')
            : result.nextAction === 'find_workers'
              ? t('Work posted. Review matching workers and applicants next.')
              : result.nextAction === 'edit_draft'
                ? t('Work draft saved.')
                : t('Work posted.'))
          if (result.nextAction === 'find_workers') setShowJobDrawer(true)
          void refreshJobs()
        }}
      />

      <FormModal
        title={editingJob ? (industryLabels.explicitIndustry ? t('Edit {resource}', { resource: industryTermLower('job') }) : t('Edit job/order')) : (industryLabels.explicitIndustry ? createJobLabel : t('Create job/order'))}
        description={industryLabels.explicitIndustry ? t('{resources} stay scoped to the selected tenant, organization, company, agency, and {location} context.', { resources: industryTerm('jobs'), location: industryTermLower('location') }) : t('Jobs stay scoped to the selected tenant, organization, company, agency, and location context.')}
        isOpen={showJobModal}
        onClose={() => { setShowJobModal(false); setEditingJob(null) }}
        size="xl"
      >
        {editingJob ? (
          <form className="stack" onSubmit={handleUpdateJob}>
            <div className="admin-form-grid">
              <label>{t('Title')}<input className="input" value={editTitle} onChange={(event) => setEditTitle(event.target.value)} disabled={!canManageJobs} /></label>
              <label>{t('Status')}<select className="input" value={statusDraft} onChange={(event) => setStatusDraft(event.target.value as JobStatus)} disabled={!canManageJobs}>{jobStatuses.map((status) => <option key={status} value={status}>{tc(status)}</option>)}</select></label>
              <label>{t('Work mode')}<select className="input" value={jobForm.workMode || 'onsite'} onChange={(event) => setJobForm({ ...jobForm, workMode: event.target.value as WorkMode })} disabled={!canManageJobs}>{workModes.map((mode) => <option key={mode} value={mode}>{tc(mode)}</option>)}</select></label>
              <label>{t('Radius km')}<input type="number" min={1} step={1} className="input" value={jobForm.matchingRadiusKm ?? ''} onChange={(event) => setJobForm({ ...jobForm, matchingRadiusKm: event.target.value ? Number(event.target.value) : null })} disabled={!canManageJobs || jobForm.workMode === 'remote'} /></label>
              {canViewPayRate ? <label>{t('Pay rate')}<input type="number" min={0} step="0.01" className="input" value={jobForm.payRate ?? ''} onChange={(event) => setJobForm({ ...jobForm, payRate: event.target.value })} disabled={!canManageJobs} /></label> : null}
              {canViewBillRate ? <label>{t('Bill rate')}<input type="number" min={0} step="0.01" className="input" value={jobForm.billRate ?? ''} onChange={(event) => setJobForm({ ...jobForm, billRate: event.target.value })} disabled={!canManageJobs} /></label> : null}
              {canViewPayRate || canViewBillRate ? <label>{t('Currency')}<input className="input" maxLength={3} value={jobForm.currencyCode || 'CAD'} onChange={(event) => setJobForm({ ...jobForm, currencyCode: event.target.value.toUpperCase() })} disabled={!canManageJobs} /></label> : null}
              <label className="admin-form-wide">{t('Description')}<textarea className="input" value={editDescription} onChange={(event) => setEditDescription(event.target.value)} disabled={!canManageJobs} /></label>
            </div>
            <p className="helper">{t('Remote: no site attendance required. Onsite: workers must be within the matching radius of the job location. Hybrid: some site attendance is required, so location matching still applies.')}</p>
            <SkillPicker selectedSkills={pickerSkills} onChange={setPickerSkills} label={t('What skills are required?')} placeholder={t('Search canonical skills')} requestContext="job_required_skills" disabled={!canManageJobs} />
            {jobSkillDrafts.length ? (
              <div className="admin-form-grid">
                {jobSkillDrafts.map((draft) => (
                  <div key={draft.skill.id} className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                    <strong>{draft.skill.canonicalName}</strong>
                    <div className="admin-form-grid">
                      <label>{t('Requirement')}<select className="input" value={draft.requirementType} onChange={(event) => setJobSkillDrafts((current) => current.map((item) => item.skill.id === draft.skill.id ? { ...item, requirementType: event.target.value as JobSkillDraft['requirementType'] } : item))} disabled={!canManageJobs}>{requirementTypes.map((type) => <option key={type} value={type}>{tc(type)}</option>)}</select></label>
                      <label>{t('Minimum level')}<select className="input" value={draft.minimumLevel} onChange={(event) => setJobSkillDrafts((current) => current.map((item) => item.skill.id === draft.skill.id ? { ...item, minimumLevel: event.target.value as JobSkillDraft['minimumLevel'] } : item))} disabled={!canManageJobs}>{minimumLevels.map((level) => <option key={level || 'none'} value={level}>{level ? tc(level) : t('None')}</option>)}</select></label>
                    </div>
                  </div>
                ))}
              </div>
            ) : parsedSkillTags.length ? <div className="admin-chip-list">{parsedSkillTags.map((tag) => <span key={tag} className="admin-chip">{t('Legacy skill')} · {tag}</span>)}</div> : null}
            <button type="submit" className="btn btn-primary" disabled={!canManageJobs || saving}>{saving ? t('Saving...') : t('Save job')}</button>
          </form>
        ) : (
          <form className="stack" onSubmit={handleCreateJob}>
            {!canManageJobs ? <p className="helper">{t('Job creation requires job create or manage permission.')}</p> : null}

            {delegatedRelationshipEntries.length > 0 ? (
              <div className="admin-form-grid">
                <label>{t('Job context')}
                  <select
                    className="input"
                    value={jobDelegatedRelId}
                    disabled={!canManageJobs}
                    onChange={(event) => {
                      const relId = event.target.value
                      setJobDelegatedRelId(relId)
                      if (!relId) {
                        setJobForm((current) => ({ ...current, companyOrgId: '', managingAgencyOrgId: '', locationId: '', locationTenantId: null }))
                      } else {
                        const entry = delegatedRelationshipEntries.find((e) => e.relationshipId === relId)
                        if (entry) {
                          setJobForm((current) => ({
                            ...current,
                            companyOrgId: entry.companyOrg?.id || '',
                            managingAgencyOrgId: entry.agencyOrg?.id || '',
                            locationId: '',
                            locationTenantId: null,
                          }))
                        }
                      }
                    }}
                  >
                    <option value="">{t('Own organization job')}</option>
                    {delegatedRelationshipEntries.map((entry) => (
                      <option key={entry.relationshipId} value={entry.relationshipId}>
                        {t('On behalf of: {company}', { company: entry.companyOrg?.name || t('Client company') })}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}

            {selectedDelegatedEntry ? (
              <p className="admin-disabled-callout" style={{ borderColor: 'var(--accent, #3b82f6)' }}>
                <strong>{t('Delegated job')}</strong> {t('Creating on behalf of')} <strong>{selectedDelegatedEntry.companyOrg?.name || t('client company')}</strong>. {t('Company, agency, and location are locked to the granted relationship scope.')}
              </p>
            ) : null}

            <div className="admin-form-grid">
              <label>{t('Posting organization')}<select className="input" value={jobForm.createdByOrgId} onChange={(event) => setJobForm({ ...jobForm, createdByOrgId: event.target.value })} disabled={!canManageJobs || Boolean(selectedDelegatedEntry)}>{organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
              <label>{t('Posting/managing agency')}<select className="input" value={jobForm.managingAgencyOrgId || ''} onChange={(event) => setJobForm({ ...jobForm, managingAgencyOrgId: event.target.value })} disabled={!canManageJobs || Boolean(selectedDelegatedEntry)}><option value="">{t('None')}</option>{agencyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
              <label>{t('Client/company *')}<select className="input" value={jobForm.companyOrgId || ''} onChange={(event) => setJobForm({ ...jobForm, companyOrgId: event.target.value })} disabled={!canManageJobs || Boolean(selectedDelegatedEntry)}><option value="">{t('Select company')}</option>{companyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
              {selectedDelegatedEntry ? (
                <label>{t('Location (granted sites) *')}
                  <select
                    className="input"
                    value={jobForm.locationId || ''}
                    disabled={!canManageJobs}
                    onChange={(event) => {
                      const locId = event.target.value
                      const grantedLoc = delegatedGrantedLocations.find((loc) => loc.locationId === locId)
                      setJobForm({ ...jobForm, locationId: locId, locationTenantId: grantedLoc?.locationTenantId || null })
                    }}
                  >
                    <option value="">{t('Select granted location')}</option>
                    {delegatedGrantedLocations.map((loc) => (
                      <option key={loc.locationId} value={loc.locationId}>
                        {loc.name || loc.locationKey || loc.locationId}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>{t('Location *')}<select className="input" value={jobForm.locationId || ''} onChange={(event) => {
                  const location = locationById.get(event.target.value)
                  const ownerOrgId = location ? getLocationOrgId(location) : ''
                  setJobForm({ ...jobForm, locationId: event.target.value, locationTenantId: null, companyOrgId: !jobForm.companyOrgId && getOrgType(ownerOrgId) === 'company' ? ownerOrgId : jobForm.companyOrgId })
                }} disabled={!canManageJobs}>
                  <option value="">{industryLabels.explicitIndustry ? t('Select {resource}', { resource: industryTermLower('location') }) : t('Select location')}</option>
                  {groupLocations(jobLocationOptions).map(([group, locations]) => (
                    <optgroup key={group} label={group}>
                      {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
                    </optgroup>
                  ))}
                </select></label>
              )}
              <label>{t('Job title *')}<input className="input" value={jobForm.title} onChange={(event) => setJobForm({ ...jobForm, title: event.target.value })} disabled={!canManageJobs} /></label>
              <label>{t('Job type')}<input className="input" value={jobForm.jobType || ''} onChange={(event) => setJobForm({ ...jobForm, jobType: event.target.value })} disabled={!canManageJobs} /></label>
              <label>{t('Status')}<select className="input" value={jobForm.status} onChange={(event) => setJobForm({ ...jobForm, status: event.target.value as JobStatus })} disabled={!canManageJobs}>{jobStatuses.map((status) => <option key={status} value={status}>{tc(status)}</option>)}</select></label>
              <label>{t('Visibility')}<select className="input" value={jobForm.visibilityType} onChange={(event) => setJobForm({ ...jobForm, visibilityType: event.target.value as JobVisibilityType })} disabled={!canManageJobs}>{visibilityTypes.map((type) => <option key={type} value={type}>{tc(type)}</option>)}</select></label>
              <label>{t('Work mode')}<select className="input" value={jobForm.workMode || 'onsite'} onChange={(event) => setJobForm({ ...jobForm, workMode: event.target.value as WorkMode })} disabled={!canManageJobs}>{workModes.map((mode) => <option key={mode} value={mode}>{tc(mode)}</option>)}</select></label>
              <label>{t('Radius km')}<input type="number" min={1} step={1} className="input" value={jobForm.matchingRadiusKm ?? ''} onChange={(event) => setJobForm({ ...jobForm, matchingRadiusKm: event.target.value ? Number(event.target.value) : null })} disabled={!canManageJobs || jobForm.workMode === 'remote'} /></label>
              {canViewPayRate ? <label>{t('Pay rate')}<input type="number" min={0} step="0.01" className="input" value={jobForm.payRate ?? ''} onChange={(event) => setJobForm({ ...jobForm, payRate: event.target.value })} disabled={!canManageJobs} /></label> : null}
              {canViewBillRate ? <label>{t('Bill rate')}<input type="number" min={0} step="0.01" className="input" value={jobForm.billRate ?? ''} onChange={(event) => setJobForm({ ...jobForm, billRate: event.target.value })} disabled={!canManageJobs} /></label> : null}
              {canViewPayRate || canViewBillRate ? <label>{t('Currency')}<input className="input" maxLength={3} value={jobForm.currencyCode || 'CAD'} onChange={(event) => setJobForm({ ...jobForm, currencyCode: event.target.value.toUpperCase() })} disabled={!canManageJobs} /></label> : null}
              <label className="admin-check-row"><input type="checkbox" checked={Boolean(jobForm.approvalRequired)} onChange={(event) => setJobForm({ ...jobForm, approvalRequired: event.target.checked })} disabled={!canManageJobs} /> {t('Company approval required')}</label>
              <label className="admin-form-wide">{t('Description')}<textarea className="input" value={jobForm.description || ''} onChange={(event) => setJobForm({ ...jobForm, description: event.target.value })} disabled={!canManageJobs} /></label>
            </div>
            <p className="helper">{t('Remote: no site attendance required. Onsite: workers must be within the matching radius of the job location. Hybrid: some site attendance is required, so location matching still applies.')}</p>
            <BrandPair
              primary={brandForOrg(jobForm.companyOrgId, t('Company not selected'))}
              secondary={brandForOrg(jobForm.managingAgencyOrgId || jobForm.createdByOrgId, t('Agency not selected'))}
              primaryLabel={t('For')}
              secondaryLabel={t('Posted by')}
            />
            <SkillPicker selectedSkills={pickerSkills} onChange={setPickerSkills} label={t('What skills are required?')} placeholder={t('Search canonical skills')} requestContext="job_required_skills" disabled={!canManageJobs} />
            {jobSkillDrafts.length ? (
              <div className="admin-form-grid">
                {jobSkillDrafts.map((draft) => (
                  <div key={draft.skill.id} className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
                    <strong>{draft.skill.canonicalName}</strong>
                    <div className="admin-form-grid">
                      <label>{t('Requirement')}<select className="input" value={draft.requirementType} onChange={(event) => setJobSkillDrafts((current) => current.map((item) => item.skill.id === draft.skill.id ? { ...item, requirementType: event.target.value as JobSkillDraft['requirementType'] } : item))} disabled={!canManageJobs}>{requirementTypes.map((type) => <option key={type} value={type}>{tc(type)}</option>)}</select></label>
                      <label>{t('Minimum level')}<select className="input" value={draft.minimumLevel} onChange={(event) => setJobSkillDrafts((current) => current.map((item) => item.skill.id === draft.skill.id ? { ...item, minimumLevel: event.target.value as JobSkillDraft['minimumLevel'] } : item))} disabled={!canManageJobs}>{minimumLevels.map((level) => <option key={level || 'none'} value={level}>{level ? tc(level) : t('None')}</option>)}</select></label>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {!selectedDelegatedEntry && jobLocationOptions.length === 0 ? <span className="error-text">{t('No locations available for this company. Create a location/fence first.')}</span> : null}
            {selectedDelegatedEntry && delegatedGrantedLocations.length === 0 ? <span className="error-text">{t('No locations with job creation permission are granted for this relationship.')}</span> : null}
            {locationConflict ? <span className="error-text">{t('Selected location does not match the selected company or agency.')}</span> : null}
            <button type="submit" className="btn btn-primary" disabled={!canManageJobs || saving || locationConflict}>{saving ? t('Saving...') : createJobLabel}</button>
          </form>
        )}
      </FormModal>

      <DetailDrawer
        title={selectedJob?.title || t('Job/order')}
        subtitle={selectedJob ? `${tc(selectedJob.status)} / ${tc(selectedJob.visibility_type)} / ${selectedJobLocationLabel}` : undefined}
        isOpen={showJobDrawer && Boolean(selectedJob)}
        onClose={() => setShowJobDrawer(false)}
        actions={selectedJob ? (
          <>
            <button type="button" className="btn btn-secondary" disabled={!canManageJobs} title={!canManageJobs ? t('Job management permission is required.') : undefined} onClick={() => openEditJob(selectedJob)}>{t('Edit')}</button>
            <button type="button" className="btn btn-primary" disabled={!canManageJobs || isHistoricalLifecycle(jobLifecycleStatus(selectedJob))} title={!canManageJobs ? t('Job management permission is required.') : isHistoricalLifecycle(jobLifecycleStatus(selectedJob)) ? t('Historical jobs cannot receive new shifts.') : undefined} onClick={() => openCreateShift()}>{t('Add shift')}</button>
            <button type="button" className="btn btn-secondary" disabled={!canManageAssignments || isHistoricalLifecycle(jobLifecycleStatus(selectedJob))} title={!canManageAssignments ? t('Assignment management permission is required.') : isHistoricalLifecycle(jobLifecycleStatus(selectedJob)) ? t('Historical jobs cannot receive new assignments.') : undefined} onClick={() => setShowAssignmentModal(true)}>{assignWorkerLabel}</button>
          </>
        ) : null}
      >
        {selectedJob ? (
          <div className="stack">
            <BrandPair primary={jobCompanyBrand(selectedJob)} secondary={jobAgencyBrand(selectedJob)} />
            {canManageJobs?<NativeJobClassifications key={selectedJob.id} jobId={selectedJob.id}/>:null}
            <div className="admin-chip-list">
              {statusBadge(jobLifecycleStatus(selectedJob), tc(selectedJob.status), t)}
              <span className="admin-chip">{tc(selectedJob.visibility_type)}</span>
              <span className="admin-chip">{tc(selectedJob.work_mode || 'onsite')}</span>
              {canViewPayRate ? <span className="admin-chip">{t('Pay {rate}', { rate: rate(selectedJob.pay_rate, selectedJob.currency_code || 'CAD') })}</span> : null}
              {canViewBillRate ? <span className="admin-chip">{t('Bill {rate}', { rate: rate(selectedJob.bill_rate, selectedJob.currency_code || 'CAD') })}</span> : null}
              {selectedJob.work_mode !== 'remote' ? <span className="admin-chip">{t('{km} km radius', { km: selectedJob.matching_radius_km || 25 })}</span> : null}
              <span className="admin-chip">{t('{count} shifts', { count: shifts.length })}</span>
              <span className="admin-chip">{t('{count} assignments', { count: assignments.length })}</span>
            </div>
            <div className="admin-filter-row">
              <label>{t('Lifecycle view')}<select className="input" value={detailLifecycle} onChange={(event) => setDetailLifecycle(event.target.value as LifecycleFilter)}>
                <option value="active">{t('Active rows')}</option>
                <option value="history">{t('History')}</option>
                <option value="all">{t('All rows')}</option>
              </select></label>
            </div>
            <section className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
              <h4>{t('Overview')}</h4>
              <p className="helper">{selectedJob.description || t('No description provided.')}</p>
              <p><strong>{t('Posted by agency')}</strong><br />{selectedJob.managing_agency_org_name || selectedJob.created_by_org_name || organizationLabel(organizations, selectedJob.managing_agency_org_id || selectedJob.created_by_org_id) || '-'}</p>
              <p><strong>{t('For company/client')}</strong><br />{selectedJob.company_org_name || organizationLabel(organizations, selectedJob.company_org_id) || '-'}</p>
              <p><strong>{t('Location')}</strong><br />{selectedJobLocationLabel}</p>
              <div className="admin-chip-list">{renderJobSkillChips(selectedJob).length ? renderJobSkillChips(selectedJob) : <span className="helper">{t('No required skills listed.')}</span>}</div>
            </section>

            <section className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
              <div className="admin-panel-toolbar">
                <h4 style={{ margin: 0 }}>{t('Shifts')}</h4>
                <button type="button" className="btn btn-secondary" disabled={!canManageJobs || isHistoricalLifecycle(jobLifecycleStatus(selectedJob))} title={!canManageJobs ? t('Job management permission is required.') : isHistoricalLifecycle(jobLifecycleStatus(selectedJob)) ? t('Historical jobs cannot receive new shifts.') : undefined} onClick={() => openCreateShift()}>{t('Add shift')}</button>
              </div>
              <table className="workspace-table">
                <thead><tr><th>{t('Time')}</th><th>{t('Location')}</th><th>{t('Mode')}</th>{canViewPayRate || canViewBillRate ? <th>{t('Rates')}</th> : null}<th>{t('Need')}</th><th>{t('Status')}</th><th>{t('Assigned')}</th><th>{t('Actions')}</th></tr></thead>
                <tbody>
                  {visibleShifts.map((shift) => {
                    const assignedWorkers = assignments.filter((assignment) => assignment.job_shift_id === shift.id)
                    const lifecycleStatus = shiftLifecycleStatus(shift)
                    return (
                      <tr key={shift.id}>
                        <td>{shiftLabel(shift)}</td>
                        <td>{shift.location_id ? shift.location_name || locationLabel(locationById.get(shift.location_id || '')) || t('Location selected, name unavailable') : t('Job location')}</td>
                        <td>{tc(shift.work_mode || selectedJob.work_mode || 'onsite')}{(shift.work_mode || selectedJob.work_mode) !== 'remote' ? ` / ${t('{km} km', { km: shift.matching_radius_km || selectedJob.matching_radius_km || 25 })}` : ''}</td>
                        {canViewPayRate || canViewBillRate ? <td>{canViewPayRate ? rate(shift.resolved_pay_rate || shift.pay_rate || selectedJob.pay_rate, shift.resolved_currency_code || shift.currency_code || selectedJob.currency_code || 'CAD') : null}{canViewBillRate ? <div className="helper">{t('Bill {rate}', { rate: rate(shift.resolved_bill_rate || shift.bill_rate || selectedJob.bill_rate, shift.resolved_currency_code || shift.currency_code || selectedJob.currency_code || 'CAD') })}</div> : null}</td> : null}
                        <td>{shift.workers_needed}</td>
                        <td>{statusBadge(lifecycleStatus, tc(shift.status), t)}</td>
                        <td>{assignedWorkers.length ? assignedWorkers.map((assignment) => assignment.worker_display_name || assignment.worker_email || (industryLabels.explicitIndustry ? industryTerm('worker') : t('Worker'))).join(', ') : t('{count} assigned', { count: shift.confirmed_assignments_count || 0 })}</td>
                        <td>
                          <EntityActionMenu>
                            <button type="button" className="btn btn-ghost" disabled={!canManageJobs} title={!canManageJobs ? t('Job management permission is required.') : undefined} onClick={() => openEditShift(shift)}>{t('Edit')}</button>
                            <button type="button" className="btn btn-ghost" disabled={!canManageJobs || saving || isHistoricalLifecycle(lifecycleStatus) || Boolean(shift.published_at)} title={!canManageJobs ? t('Job management permission is required.') : shift.published_at ? t('This shift is already published.') : isHistoricalLifecycle(lifecycleStatus) ? t('Historical shifts cannot be published.') : undefined} onClick={() => setConfirmAction({ title: t('Publish shift'), message: t('Publish this shift as an open shift workers can apply to?'), confirmLabel: t('Publish'), onConfirm: async () => handlePublishShift(shift) })}>{t('Publish')}</button>
                            <button type="button" className="btn btn-ghost" disabled={!canManageJobs || saving || isHistoricalLifecycle(lifecycleStatus)} title={!canManageJobs ? t('Job management permission is required.') : isHistoricalLifecycle(lifecycleStatus) ? t('Historical shifts cannot be duplicated from this active workflow.') : undefined} onClick={() => setConfirmAction({ title: t('Duplicate shift'), message: t('Duplicate this shift for the same job?'), confirmLabel: t('Duplicate'), onConfirm: async () => handleDuplicateShift(shift) })}>{t('Duplicate')}</button>
                            <button type="button" className="btn btn-ghost" disabled={!canManageJobs || saving || isHistoricalLifecycle(lifecycleStatus)} title={!canManageJobs ? t('Job management permission is required.') : isHistoricalLifecycle(lifecycleStatus) ? t('This shift is already historical or unavailable.') : undefined} onClick={() => setConfirmAction({ title: t('Cancel shift'), message: t('Cancel this shift? Existing punch/timekeeping records are not changed by this UI action.'), confirmLabel: t('Cancel shift'), destructive: true, onConfirm: async () => handleCancelShift(shift) })}>{t('Cancel')}</button>
                            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Cancel = stop future use for this job shift.'), (reason) => cancelSystemJobShift(shift.id, reason), t('Shift cancelled.'))}>{t('System cancel')}</button>
                            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Archive = hide this job shift from normal operations but keep history.'), (reason) => archiveJobShift(shift.id, reason), t('Shift archived.'))}>{t('Archive')}</button>
                            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Restore this job shift to operational use.'), (reason) => restoreJobShift(shift.id, reason), t('Shift restored.'))}>{t('Restore')}</button>
                            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runDeleteWithImpact(t('Delete job shift'), () => getJobShiftDeleteImpact(shift.id), (reason) => deleteJobShift(shift.id, reason), t('Shift deleted.'))}>{t('Delete')}</button>
                            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Purge demo data = irreversible cleanup for demo/test shift records only.'), (reason) => purgeDemoJobShift(shift.id, reason), t('Demo shift data purged.'), { destructive: true, purge: true })}>{t('Purge demo data')}</button>
                          </EntityActionMenu>
                        </td>
                      </tr>
                    )
                  })}
                  {visibleShifts.length === 0 ? <tr><td colSpan={canViewPayRate || canViewBillRate ? 8 : 7}><EmptyState title={detailLifecycle === 'history' ? t('No historical shifts') : t('No active shifts')} description={detailLifecycle === 'active' ? t('Completed, cancelled, expired, and unavailable shifts are hidden from this view.') : t('No shifts match this lifecycle view.')} /></td></tr> : null}
                </tbody>
              </table>
            </section>

            <section className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
              <div className="admin-panel-toolbar">
                <h4 style={{ margin: 0 }}>{t('Assignments')}</h4>
                <button type="button" className="btn btn-secondary" disabled={!canManageAssignments} onClick={() => setShowAssignmentModal(true)}>{t('Assign worker')}</button>
              </div>
              <table className="workspace-table">
                <thead><tr><th>{industryLabels.explicitIndustry ? industryTerm('worker') : t('Worker')}</th><th>{t('Brand context')}</th><th>{t('Status')}</th><th>{t('Lifecycle / ETA')}</th>{canViewPayRate || canViewBillRate ? <th>{t('Rates')}</th> : null}<th>{t('Actions')}</th></tr></thead>
                <tbody>
                  {visibleAssignments.map((assignment) => {
                    const lifecycleStatus = assignmentLifecycleStatus(assignment)
                    return (
                    <tr key={assignment.id}>
                      <td>{assignment.worker_display_name || assignment.worker_email || workerLabel(workerOptions.find((worker) => worker.worker_profile_id === assignment.worker_profile_id)) || (industryLabels.explicitIndustry ? industryTerm('worker') : t('Worker'))}</td>
                      <td><BrandPair primary={brandForOrg(assignment.company_org_id || selectedJob.company_org_id, assignment.company_org_name || selectedJob.company_org_name)} secondary={brandForOrg(assignment.supplying_agency_org_id || selectedJob.managing_agency_org_id, assignment.supplying_agency_org_name || selectedJob.managing_agency_org_name)} /></td>
                      <td>
                        {statusBadge(lifecycleStatus, tc(assignment.status), t)}
                        <select className="input admin-inline-select" value={assignmentStatusDrafts[assignment.id] || assignment.status} onChange={(event) => setAssignmentStatusDrafts({ ...assignmentStatusDrafts, [assignment.id]: event.target.value as JobAssignmentStatus })} disabled={!canManageAssignments} title={!canManageAssignments ? t('Assignment management permission is required.') : undefined}>{assignmentStatuses.map((status) => <option key={status} value={status}>{tc(status)}</option>)}</select>
                      </td>
                      <td>
                        {assignment.lifecycleStatus || assignment.lifecycle_status ? (
                          <AssignmentLifecycleBadge
                            status={assignment.lifecycleStatus || assignment.lifecycle_status}
                            exception={assignment.attendanceException || assignment.attendance_exception}
                            eta={assignment.eta}
                            updatedAt={assignment.lifecycleUpdatedAt || assignment.lifecycle_updated_at}
                            t={t}
                            testId="admin-assignment-lifecycle"
                          />
                        ) : assignment.on_the_way_at ? <span className="admin-chip">{t('On the way {time}', { time: new Date(assignment.on_the_way_at).toLocaleTimeString() })}</span> : <span className="helper">{t('Not marked')}</span>}
                      </td>
                      {canViewPayRate || canViewBillRate ? <td>
                        <div className="admin-form-grid" style={{ minWidth: 280 }}>
                          {canViewPayRate ? <input aria-label={t('Pay override')} className="input" type="number" min={0} step="0.01" placeholder={rateLabel(assignment.resolved_pay_rate, assignment.resolved_currency_code || assignment.rate_currency)} value={assignmentRateDrafts[assignment.id]?.payRate || ''} onChange={(event) => setAssignmentRateDrafts({ ...assignmentRateDrafts, [assignment.id]: { ...(assignmentRateDrafts[assignment.id] || { billRate: '', rateCurrency: 'CAD' }), payRate: event.target.value } })} disabled={!canManageAssignments} /> : null}
                          {canViewBillRate ? <input aria-label={t('Bill override')} className="input" type="number" min={0} step="0.01" placeholder={rateLabel(assignment.resolved_bill_rate, assignment.resolved_currency_code || assignment.rate_currency)} value={assignmentRateDrafts[assignment.id]?.billRate || ''} onChange={(event) => setAssignmentRateDrafts({ ...assignmentRateDrafts, [assignment.id]: { ...(assignmentRateDrafts[assignment.id] || { payRate: '', rateCurrency: 'CAD' }), billRate: event.target.value } })} disabled={!canManageAssignments} /> : null}
                          <input className="input" maxLength={3} placeholder={assignment.resolved_currency_code || assignment.rate_currency || 'CAD'} value={assignmentRateDrafts[assignment.id]?.rateCurrency || ''} onChange={(event) => setAssignmentRateDrafts({ ...assignmentRateDrafts, [assignment.id]: { ...(assignmentRateDrafts[assignment.id] || { payRate: '', billRate: '' }), rateCurrency: event.target.value.toUpperCase() } })} disabled={!canManageAssignments} />
                        </div>
                        <div className="helper">{[canViewPayRate ? t('Resolved pay {pay}', { pay: rate(assignment.resolved_pay_rate, assignment.resolved_currency_code || assignment.rate_currency) }) : '', canViewBillRate ? t('Bill {rate}', { rate: rate(assignment.resolved_bill_rate, assignment.resolved_currency_code || assignment.rate_currency) }) : ''].filter(Boolean).join(' / ')}</div>
                      </td> : null}
                      <td>
                        <EntityActionMenu>
                          <button type="button" className="btn btn-ghost" disabled={!canManageAssignments || saving || isHistoricalLifecycle(lifecycleStatus)} title={!canManageAssignments ? t('Assignment management permission is required.') : isHistoricalLifecycle(lifecycleStatus) ? t('Historical assignments cannot be changed from this active workflow.') : undefined} onClick={() => setConfirmAction({ title: t('Update assignment'), message: t('Save assignment status and rate overrides?'), confirmLabel: t('Save assignment'), onConfirm: async () => runAction(async () => {
                            const draft = assignmentRateDrafts[assignment.id] || { payRate: '', billRate: '', rateCurrency: assignment.rate_currency || 'CAD' }
                            await updateJobAssignment(assignment.id, {
                              status: assignmentStatusDrafts[assignment.id] || assignment.status,
                              ...(canViewPayRate ? { payRate: rateNumber(draft.payRate) } : {}),
                              ...(canViewBillRate ? { billRate: rateNumber(draft.billRate) } : {}),
                              ...((canViewPayRate || canViewBillRate) ? { rateCurrency: currencyValue(draft.rateCurrency || assignment.rate_currency || 'CAD') } : {}),
                            })
                            await refreshSelectedJob()
                          }, t('Assignment updated.')) })}>{t('Save assignment')}</button>
                          {canApproveAssignments ? <button type="button" className="btn btn-ghost" disabled={saving || isHistoricalLifecycle(lifecycleStatus) || assignment.status !== 'pending_company_approval'} title={assignment.status !== 'pending_company_approval' ? t('Only assignments pending company approval can be approved.') : isHistoricalLifecycle(lifecycleStatus) ? t('Historical assignments cannot be approved.') : undefined} onClick={() => setConfirmAction({ title: t('Approve assignment'), message: t('Approve this worker assignment for the company?'), confirmLabel: t('Approve'), onConfirm: async () => runAction(async () => { await approveJobAssignment(assignment.id); await refreshSelectedJob() }, t('Assignment approved.')) })}>{t('Approve')}</button> : null}
                          <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Cancel = stop future use for this assignment.'), (reason) => cancelJobAssignment(assignment.id, reason), t('Assignment cancelled.'))}>{t('Cancel')}</button>
                          <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Revoke this assignment while keeping its history.'), (reason) => revokeJobAssignment(assignment.id, reason), t('Assignment revoked.'))}>{t('Revoke')}</button>
                          <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Restore this assignment to operational use.'), (reason) => restoreJobAssignment(assignment.id, reason), t('Assignment restored.'))}>{t('Restore')}</button>
                          <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runDeleteWithImpact(t('Delete assignment'), () => getJobAssignmentDeleteImpact(assignment.id), (reason) => deleteJobAssignment(assignment.id, reason), t('Assignment deleted.'))}>{t('Delete')}</button>
                          <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => runOperationalLifecycle(t('Purge demo data = irreversible cleanup for demo/test assignment records only.'), (reason) => purgeDemoJobAssignment(assignment.id, reason), t('Demo assignment data purged.'), { destructive: true, purge: true })}>{t('Purge demo data')}</button>
                        </EntityActionMenu>
                      </td>
                    </tr>
                  )})}
                  {visibleAssignments.length === 0 ? <tr><td colSpan={canViewPayRate || canViewBillRate ? 6 : 5}><EmptyState title={detailLifecycle === 'history' ? t('No historical assignments') : t('No active assignments')} description={detailLifecycle === 'active' ? t('Completed, cancelled, and unavailable assignments are hidden from this view.') : t('No assignments match this lifecycle view.')} /></td></tr> : null}
                </tbody>
              </table>
            </section>

            <section className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
              <div className="admin-panel-toolbar">
                <h4 style={{ margin: 0 }}>{t('Applications / matches')}</h4>
                <button type="button" className="btn btn-secondary" disabled={!canManageApplications} onClick={() => setShowApplicationModal(true)}>{t('Create application')}</button>
              </div>
              <div className="admin-filter-row">
                <select className="input" value={selectedMatchShift?.id || ''} onChange={(event) => { setSelectedMatchShiftId(event.target.value); refreshShiftMatches(event.target.value).catch((err: any) => setError(apiErrorMessage(err, t('Unable to load matched workers.')))) }}>
                  {shifts.map((shift) => <option key={shift.id} value={shift.id}>{shiftLabel(shift)}</option>)}
                </select>
                <input className="input" value={matchSearch} onChange={(event) => setMatchSearch(event.target.value)} placeholder={t('Search workers, skills, status')} />
                <button type="button" className="btn btn-secondary" onClick={() => refreshShiftMatches()} disabled={!selectedMatchShift || saving}>{t('Refresh')}</button>
              </div>
              <div className="admin-card-grid" style={{ marginBottom: '1rem' }}>
                {rankedApplicants.slice(0, 4).map((applicant) => {
                  const score = applicant.reliability_score == null ? null : Math.round(Number(applicant.reliability_score))
                  const highReliability = score !== null && score >= 85
                  const blockingConflicts = (applicant.conflicts || []).filter((conflict) => conflict.severity !== 'warning')
                  return (
                    <article key={applicant.id} className="panel workspace-panel stack" style={{ background: 'var(--surface)' }}>
                      <div className="admin-panel-toolbar">
                        <strong>{applicant.worker_display_name || applicant.worker_email || t('Applicant')}</strong>
                        <span className={`status-pill status-pill--${highReliability ? 'approved' : applicant.status}`}>{score === null ? t('Reliability pending') : t('{score} reliability', { score })}</span>
                      </div>
                      <div className="admin-chip-list">
                        <span className="admin-chip">{['submitted', 'shortlisted'].includes(applicant.status) ? t('Awaiting approval') : tc(applicant.status)}</span>
                        {applicant.attendance_score != null ? <span className="admin-chip">{t('Attendance {score}%', { score: Math.round(Number(applicant.attendance_score)) })}</span> : null}
                        {applicant.punctuality_score != null ? <span className="admin-chip">{t('Punctuality {score}%', { score: Math.round(Number(applicant.punctuality_score)) })}</span> : null}
                        {applicant.conflict_count ? <span className="admin-chip">{t('Conflicts {count}', { count: applicant.conflict_count })}</span> : <span className="admin-chip">{t('No conflicts')}</span>}
                        {blockingConflicts.length ? <span className="admin-chip">{t('Not eligible')}</span> : <span className="admin-chip">{t('Eligible')}</span>}
                        {(applicant.conflicts || []).map((conflict, index) => <span key={`${applicant.id}-conflict-${index}`} className="admin-chip">{t(conflictLabel(conflict))}</span>)}
                      </div>
                      {(applicant.conflicts || []).slice(0, 3).map((conflict, index) => <p key={index} className="helper">{t(conflictSeverity(conflict))}: {t(conflictLabel(conflict))}{conflict.message ? ` - ${conflict.message}` : ''}</p>)}
                      <div className="inline-group">
                        <button type="button" className="btn btn-secondary" disabled={!canManageAssignments || saving || !['submitted', 'shortlisted'].includes(applicant.status) || Boolean(applicant.approval_disabled_reason)} title={applicant.approval_disabled_reason || (blockingConflicts.length ? blockingConflicts.map((conflict) => conflict.message).join(' ') : undefined)} onClick={() => setConfirmAction({ title: t('Approve applicant'), message: t('Approve {worker} for this shift?', { worker: applicant.worker_display_name || applicant.worker_email || t('this applicant') }), confirmLabel: t('Approve'), onConfirm: async () => runAction(async () => { await approveShiftApplication(applicant.id); await refreshSelectedJob(); await refreshRankedApplicants() }, t('Application approved.')) })}>{t('Approve')}</button>
                        <button type="button" className="btn btn-ghost" disabled={!canManageApplications || saving || !['submitted', 'shortlisted'].includes(applicant.status)} onClick={() => setConfirmAction({ title: t('Reject applicant'), message: t('Reject this open-shift application?'), confirmLabel: t('Reject'), onConfirm: async () => runAction(async () => { await rejectShiftApplication(applicant.id); await refreshSelectedJob(); await refreshRankedApplicants() }, t('Application rejected.')) })}>{t('Reject')}</button>
                      </div>
                    </article>
                  )
                })}
              </div>
              <table className="workspace-table">
                <thead><tr><th>{industryLabels.explicitIndustry ? industryTerm('worker') : t('Worker')}</th><th>{t('Skills')}</th><th>{t('Match')}</th><th>{t('Application')}</th><th>{industryLabels.explicitIndustry ? industryTerm('assignment') : t('Assignment')}</th><th>{t('Actions')}</th></tr></thead>
                <tbody>
                  {filteredShiftMatches.map((match) => {
                    const assignmentBlockReason = localizedMatchAssignmentBlockReason(t, match, selectedMatchShift)
                    const applicationBlockReason = matchIsEligible(match) ? '' : (assignmentBlockReason || t('Missing required skills'))
                    const diagnosticChips = renderMatchDiagnosticChips(t, match)
                    return (
                      <tr key={match.worker_profile_id}>
                        <td><strong>{matchWorkerLabel(match)}</strong><div className="helper">{match.worker_email || match.worker_phone || t('No contact shown')} / {tc(match.current_status)}</div></td>
                        <td><div className="admin-chip-list">{skillChips(match.skill_names)}{(match.skill_names || []).length === 0 ? <span className="helper">{t('No skills yet')}</span> : null}</div></td>
                        <td><span className="admin-chip">{match.match_score ?? 0}%</span>{matchIsEligible(match) ? <span className="admin-chip">{t('Eligible')}</span> : <span className="admin-chip">{t('Not eligible')}</span>}<div className="helper">{match.match_reason || t('Skill match')}</div>{localizedMatchDistanceDiagnostic(t, match) ? <div className="helper">{localizedMatchDistanceDiagnostic(t, match)}</div> : null}{diagnosticChips.length ? <div className="admin-chip-list">{diagnosticChips}</div> : null}</td>
                        <td>{match.application_status ? <span className={statusClass(match.application_status)}>{tc(match.application_status)}</span> : <span className="helper">{t('Not applied')}</span>}</td>
                        <td>{match.assignment_status ? <span className={statusClass(match.assignment_status)}>{tc(match.assignment_status)}</span> : <span className="helper">{t('Not assigned')}</span>}</td>
                        <td>
                          <EntityActionMenu>
                            <button type="button" className="btn btn-ghost" onClick={() => handleCreateApplicationFromMatch(match)} disabled={!canManageApplications || saving || Boolean(match.application_id) || Boolean(applicationBlockReason)} title={applicationBlockReason || undefined}>{t('Create application')}</button>
                            <button type="button" className="btn btn-ghost" onClick={() => setConfirmAction({ title: assignWorkerLabel, message: t('Assign {worker} to this shift?', { worker: matchWorkerLabel(match) }), confirmLabel: t('Assign'), onConfirm: async () => handleCreateAssignmentFromMatch(match) })} disabled={!canManageAssignments || saving || Boolean(assignmentBlockReason)} title={assignmentBlockReason || undefined}>{t('Assign')}</button>
                          </EntityActionMenu>
                        </td>
                    </tr>
                  )})}
                  {filteredShiftMatches.length === 0 ? <tr><td colSpan={6}>{t('No matched workers yet.')}</td></tr> : null}
                </tbody>
              </table>
            </section>

            <section className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-panel-toolbar">
                <h4 style={{ margin: 0 }}>{t('Branding/context')}</h4>
                <button type="button" className="btn btn-secondary" disabled={!canManageJobs} onClick={() => setShowVisibilityModal(true)}>{t('Add visibility')}</button>
              </div>
              <BrandPair primary={jobCompanyBrand(selectedJob)} secondary={jobAgencyBrand(selectedJob)} />
              <div className="admin-chip-list">
                {visibilityRules.map((rule) => (
                  <span key={rule.id} className="admin-chip">
                    {tc(rule.visibility_scope)}: {rule.visible_to_org_name || workerLabel(workerOptions.find((worker) => worker.worker_profile_id === rule.visible_to_worker_profile_id)) || t('Marketplace')}
                    <button type="button" className="btn btn-ghost" onClick={() => setConfirmAction({ title: t('Remove visibility'), message: t('Remove this job visibility rule?'), confirmLabel: t('Remove'), destructive: true, onConfirm: async () => runAction(async () => { await deleteJobVisibilityRule(rule.id); await refreshSelectedJob() }, t('Visibility rule removed.')) })} disabled={!canManageJobs || saving}>{t('Remove')}</button>
                  </span>
                ))}
                {visibilityRules.length === 0 ? <span className="helper">{t('No visibility rules yet.')}</span> : null}
              </div>
            </section>
          </div>
        ) : null}
      </DetailDrawer>
      <FormModal
        title={editingShift ? t('Edit shift') : t('Create shift')}
        description={t('Shift changes stay inside the selected job/order.')}
        isOpen={showShiftModal && Boolean(selectedJob)}
        onClose={() => { setShowShiftModal(false); setEditingShift(null) }}
        size="lg"
      >
        <form className="stack" onSubmit={handleShiftModalSubmit}>
          {!editingShift && shifts.length ? (
            <div className="inline-group">
              <button type="button" className="btn btn-secondary" disabled={!canManageJobs} onClick={() => {
                const previous = [...shifts].sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime())[0]
                if (previous) {
                  const start = new Date(new Date(previous.starts_at).getTime() + 24 * 60 * 60 * 1000)
                  const end = new Date(new Date(previous.ends_at).getTime() + 24 * 60 * 60 * 1000)
                  setShiftForm({
                    locationId: previous.location_id || selectedJob?.location_id || '',
                    startsAt: toInputDateTime(start.toISOString()),
                    endsAt: toInputDateTime(end.toISOString()),
                    workersNeeded: previous.workers_needed,
                    status: 'open',
                    workMode: previous.work_mode || '',
                    matchingRadiusKm: previous.matching_radius_km ?? '',
                    payRate: rateValue(previous.pay_rate),
                    billRate: rateValue(previous.bill_rate),
                    currencyCode: previous.currency_code || '',
                  })
                }
              }}>{t('Copy previous shift')}</button>
            </div>
          ) : null}
          <div className="admin-form-grid">
            <label>{t('Location')}<select className="input" value={shiftForm.locationId} onChange={(event) => setShiftForm({ ...shiftForm, locationId: event.target.value })} disabled={!canManageJobs}><option value="">{t('Job location')}</option>{localizedGroupLocations(shiftLocationOptions, selectedJob?.location_id).map(([group, locations]) => (
              <optgroup key={group} label={group}>{locations.map((location) => <option key={location.id} value={location.id}>{location.id === selectedJob?.location_id ? t('Job location - {location}', { location: locationLabel(location) }) : locationLabel(location)}</option>)}</optgroup>
            ))}</select></label>
            <label>{t('Starts *')}<input type="datetime-local" className="input" value={shiftForm.startsAt} onChange={(event) => setShiftForm({ ...shiftForm, startsAt: event.target.value })} disabled={!canManageJobs} /></label>
            <label>{t('Ends *')}<input type="datetime-local" className="input" value={shiftForm.endsAt} onChange={(event) => setShiftForm({ ...shiftForm, endsAt: event.target.value })} disabled={!canManageJobs} /></label>
            <label>{industryLabels.explicitIndustry ? t('{workers} needed *', { workers: industryTerm('workers') }) : t('Workers needed *')}<input type="number" min={1} className="input" value={shiftForm.workersNeeded} onChange={(event) => setShiftForm({ ...shiftForm, workersNeeded: Number(event.target.value) })} disabled={!canManageJobs} /></label>
            {!editingShift ? <label>{t('Repeat daily')}<input type="number" min={1} max={14} className="input" value={repeatShiftCount} onChange={(event) => setRepeatShiftCount(Math.max(1, Math.min(14, Number(event.target.value) || 1)))} disabled={!canManageJobs} /></label> : null}
            <label>{t('Status')}<select className="input" value={shiftForm.status} onChange={(event) => setShiftForm({ ...shiftForm, status: event.target.value as JobShiftStatus })} disabled={!canManageJobs}>{shiftStatuses.map((status) => <option key={status} value={status}>{tc(status)}</option>)}</select></label>
            <label>{t('Work mode')}<select className="input" value={shiftForm.workMode} onChange={(event) => setShiftForm({ ...shiftForm, workMode: event.target.value as WorkMode | '' })} disabled={!canManageJobs}><option value="">{t('Inherit job')}</option>{workModes.map((mode) => <option key={mode} value={mode}>{tc(mode)}</option>)}</select></label>
            <label>{t('Radius km')}<input type="number" min={1} step={1} className="input" value={shiftForm.matchingRadiusKm} onChange={(event) => setShiftForm({ ...shiftForm, matchingRadiusKm: event.target.value })} disabled={!canManageJobs || shiftForm.workMode === 'remote'} placeholder={String(selectedJob?.matching_radius_km || 25)} /></label>
            {canViewPayRate ? <label>{t('Pay override')}<input type="number" min={0} step="0.01" className="input" value={shiftForm.payRate} onChange={(event) => setShiftForm({ ...shiftForm, payRate: event.target.value })} disabled={!canManageJobs} placeholder={rateLabel(selectedJob?.pay_rate, selectedJob?.currency_code || 'CAD')} /></label> : null}
            {canViewBillRate ? <label>{t('Bill override')}<input type="number" min={0} step="0.01" className="input" value={shiftForm.billRate} onChange={(event) => setShiftForm({ ...shiftForm, billRate: event.target.value })} disabled={!canManageJobs} placeholder={rateLabel(selectedJob?.bill_rate, selectedJob?.currency_code || 'CAD')} /></label> : null}
            {canViewPayRate || canViewBillRate ? <label>{t('Currency override')}<input className="input" maxLength={3} value={shiftForm.currencyCode} onChange={(event) => setShiftForm({ ...shiftForm, currencyCode: event.target.value.toUpperCase() })} disabled={!canManageJobs} placeholder={selectedJob?.currency_code || 'CAD'} /></label> : null}
          </div>
          <p className="helper">{t('Work mode overrides the job setting for this shift only. Remote: no site attendance required. Onsite/Hybrid: workers must be within the matching radius of the shift or job location.')}</p>
          <button type="submit" className="btn btn-primary" disabled={!canManageJobs || saving}>{saving ? t('Saving...') : editingShift ? t('Save shift') : t('Create shift')}</button>
        </form>
      </FormModal>

      <FormModal title={t('Add visibility rule')} isOpen={showVisibilityModal && Boolean(selectedJob)} onClose={() => setShowVisibilityModal(false)} size="lg">
        <form className="stack" onSubmit={handleCreateVisibility}>
          <div className="admin-form-grid">
            <label>{t('Scope')}<select className="input" value={visibilityForm.visibilityScope} onChange={(event) => setVisibilityForm({ ...visibilityForm, visibilityScope: event.target.value as JobVisibilityScope })} disabled={!canManageJobs}>{visibilityScopes.map((scope) => <option key={scope} value={scope}>{tc(scope)}</option>)}</select></label>
            <label>{t('Visible org')}<select className="input" value={visibilityForm.visibleToOrgId} onChange={(event) => setVisibilityForm({ ...visibilityForm, visibleToOrgId: event.target.value })} disabled={!canManageJobs || ['public_marketplace', 'specific_worker'].includes(visibilityForm.visibilityScope)}><option value="">{t('None')}</option>{organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
            <label>{industryLabels.explicitIndustry ? t('Specific {worker}', { worker: industryTermLower('worker') }) : t('Specific worker')}<select className="input" value={visibilityForm.visibleToWorkerProfileId} onChange={(event) => setVisibilityForm({ ...visibilityForm, visibleToWorkerProfileId: event.target.value })} disabled={!canManageJobs || visibilityForm.visibilityScope !== 'specific_worker'}><option value="">{industryLabels.explicitIndustry ? t('Select {resource}', { resource: industryTermLower('worker') }) : t('Select worker')}</option>{workerOptions.map((worker) => <option key={worker.worker_profile_id} value={worker.worker_profile_id}>{workerLabel(worker)}</option>)}</select></label>
            <label className="admin-check-row"><input type="checkbox" checked={visibilityForm.canApply} onChange={(event) => setVisibilityForm({ ...visibilityForm, canApply: event.target.checked })} disabled={!canManageJobs} /> {t('Can apply')}</label>
            <label className="admin-check-row"><input type="checkbox" checked={visibilityForm.canAssign} onChange={(event) => setVisibilityForm({ ...visibilityForm, canAssign: event.target.checked })} disabled={!canManageJobs} /> {t('Can assign')}</label>
            <label className="admin-check-row"><input type="checkbox" checked={visibilityForm.canViewRates} onChange={(event) => setVisibilityForm({ ...visibilityForm, canViewRates: event.target.checked })} disabled={!canManageJobs} /> {t('Can view rates')}</label>
          </div>
          <button type="submit" className="btn btn-primary" disabled={!canManageJobs || saving}>{t('Add visibility')}</button>
        </form>
      </FormModal>

      <FormModal title={t('Create application')} isOpen={showApplicationModal && Boolean(selectedJob)} onClose={() => setShowApplicationModal(false)} size="lg">
        <form className="stack" onSubmit={handleCreateApplication}>
          <div className="admin-form-grid">
            <label>{industryLabels.explicitIndustry ? industryTerm('worker') : t('Worker')}<select className="input" value={applicationForm.workerProfileId} onChange={(event) => setApplicationForm({ ...applicationForm, workerProfileId: event.target.value })}><option value="">{industryLabels.explicitIndustry ? t('Select {resource}', { resource: industryTermLower('worker') }) : t('Select worker')}</option>{workerOptions.map((worker) => <option key={worker.worker_profile_id} value={worker.worker_profile_id}>{workerLabel(worker)}</option>)}</select></label>
            <label>{t('Shift')}<select className="input" value={applicationForm.jobShiftId} onChange={(event) => setApplicationForm({ ...applicationForm, jobShiftId: event.target.value })}><option value="">{t('Whole job')}</option>{shifts.map((shift) => <option key={shift.id} value={shift.id}>{shiftLabel(shift)}</option>)}</select></label>
            <label>{t('Applying org')}<select className="input" value={applicationForm.applyingThroughOrgId} onChange={(event) => setApplicationForm({ ...applicationForm, applyingThroughOrgId: event.target.value })}><option value="">{t('Direct worker')}</option>{organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
            <label className="admin-form-wide">{t('Cover note')}<textarea className="input" value={applicationForm.coverNote} onChange={(event) => setApplicationForm({ ...applicationForm, coverNote: event.target.value })} /></label>
          </div>
          <button type="submit" className="btn btn-primary" disabled={!canManageApplications || saving}>{t('Create application')}</button>
        </form>
      </FormModal>

      <FormModal title={assignWorkerLabel} isOpen={showAssignmentModal && Boolean(selectedJob)} onClose={() => setShowAssignmentModal(false)} size="lg">
        <form className="stack" onSubmit={handleCreateAssignment}>
          <div className="admin-form-grid">
            <label>{industryLabels.explicitIndustry ? industryTerm('worker') : t('Worker')}<select className="input" value={assignmentForm.workerProfileId} onChange={(event) => setAssignmentForm({ ...assignmentForm, workerProfileId: event.target.value })} disabled={!canManageAssignments}><option value="">{industryLabels.explicitIndustry ? t('Select {resource}', { resource: industryTermLower('worker') }) : t('Select worker')}</option>{workerOptions.map((worker) => <option key={worker.worker_profile_id} value={worker.worker_profile_id}>{relationshipWorkerOptionLabel(worker)}</option>)}</select></label>
            <label>{t('Shift')}<select className="input" value={assignmentForm.jobShiftId} onChange={(event) => setAssignmentForm({ ...assignmentForm, jobShiftId: event.target.value })} disabled={!canManageAssignments}><option value="">{t('Select shift')}</option>{shifts.map((shift) => <option key={shift.id} value={shift.id}>{shiftLabel(shift)}</option>)}</select></label>
            <label>{t('Application')}<select className="input" value={assignmentForm.applicationId} onChange={(event) => setAssignmentForm({ ...assignmentForm, applicationId: event.target.value })} disabled={!canManageAssignments}><option value="">{t('None')}</option>{applications.map((application) => <option key={application.id} value={application.id}>{application.worker_display_name || application.worker_email || workerLabel(workerOptions.find((worker) => worker.worker_profile_id === application.worker_profile_id)) || t('Worker application')}</option>)}</select></label>
            <label>{t('Supplying agency')}<select className="input" value={assignmentForm.supplyingAgencyOrgId} onChange={(event) => setAssignmentForm({ ...assignmentForm, supplyingAgencyOrgId: event.target.value })} disabled={!canManageAssignments}><option value="">{t('None')}</option>{agencyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
            <label>{t('Company')}<select className="input" value={assignmentForm.companyOrgId} onChange={(event) => setAssignmentForm({ ...assignmentForm, companyOrgId: event.target.value })} disabled={!canManageAssignments}><option value="">{t('Job company')}</option>{companyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
            <label>{t('Status')}<select className="input" value={assignmentForm.status} onChange={(event) => setAssignmentForm({ ...assignmentForm, status: event.target.value as JobAssignmentStatus })} disabled={!canManageAssignments}>{assignmentStatuses.map((status) => <option key={status} value={status}>{tc(status)}</option>)}</select></label>
            {canViewPayRate ? <label>{t('Pay override')}<input type="number" min={0} step="0.01" className="input" value={assignmentForm.payRate} onChange={(event) => setAssignmentForm({ ...assignmentForm, payRate: event.target.value })} disabled={!canManageAssignments} /></label> : null}
            {canViewBillRate ? <label>{t('Bill override')}<input type="number" min={0} step="0.01" className="input" value={assignmentForm.billRate} onChange={(event) => setAssignmentForm({ ...assignmentForm, billRate: event.target.value })} disabled={!canManageAssignments} /></label> : null}
            {canViewPayRate || canViewBillRate ? <label>{t('Currency')}<input className="input" maxLength={3} value={assignmentForm.rateCurrency} onChange={(event) => setAssignmentForm({ ...assignmentForm, rateCurrency: event.target.value.toUpperCase() })} disabled={!canManageAssignments} /></label> : null}
            {canViewPayRate || canViewBillRate ? <div className="helper admin-form-wide">{[canViewPayRate ? t('Resolved pay {pay}', { pay: rate(assignmentForm.payRate || shifts.find((shift) => shift.id === assignmentForm.jobShiftId)?.resolved_pay_rate || selectedJob?.pay_rate, assignmentForm.rateCurrency || selectedJob?.currency_code || 'CAD') }) : '', canViewBillRate ? t('Bill {rate}', { rate: rate(assignmentForm.billRate || shifts.find((shift) => shift.id === assignmentForm.jobShiftId)?.resolved_bill_rate || selectedJob?.bill_rate, assignmentForm.rateCurrency || selectedJob?.currency_code || 'CAD') }) : ''].filter(Boolean).join(' / ')}</div> : null}
          </div>
          <button type="submit" className="btn btn-primary" disabled={!canManageAssignments || saving}>{t('Create assignment')}</button>
        </form>
      </FormModal>

      <FormModal title={t('Skills')} isOpen={showSkillModal} onClose={() => setShowSkillModal(false)} size="md">
        <form className="stack" onSubmit={handleCreateSkill}>
          <div className="admin-form-grid">
            <label>{t('Name')}<input className="input" value={skillForm.name} onChange={(event) => setSkillForm({ ...skillForm, name: event.target.value })} disabled={!canManageSkills} /></label>
            <label>{t('Category')}<input className="input" value={skillForm.category} onChange={(event) => setSkillForm({ ...skillForm, category: event.target.value })} disabled={!canManageSkills} /></label>
            <label className="admin-form-wide">{t('Description')}<input className="input" value={skillForm.description} onChange={(event) => setSkillForm({ ...skillForm, description: event.target.value })} disabled={!canManageSkills} /></label>
          </div>
          <button type="submit" className="btn btn-secondary" disabled={!canManageSkills || saving}>{t('Create skill')}</button>
          <div className="admin-chip-list">{skills.map((skill) => <span key={skill.id} className="admin-chip">{skill.name}{skill.category ? ` / ${skill.category}` : ''}</span>)}</div>
        </form>
      </FormModal>

      <FormModal title={t('Import shifts')} description={industryLabels.explicitIndustry ? t('Existing CSV import workflow, launched from {resources}.', { resources: industryTerm('jobs') }) : t('Existing CSV import workflow, launched from Jobs / Orders.')} isOpen={showShiftImportModal} onClose={() => setShowShiftImportModal(false)} size="xl">
        <ShiftImport embedded />
      </FormModal>

      <ConfirmDialog
        title={confirmAction?.title || t('Confirm action')}
        message={confirmAction?.message || ''}
        isOpen={Boolean(confirmAction)}
        confirmLabel={confirmAction?.confirmLabel || t('Confirm')}
        destructive={Boolean(confirmAction?.destructive)}
        disabled={saving}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          const action = confirmAction
          setConfirmAction(null)
          action?.onConfirm()
        }}
      />
    </div>
  )
}
